/* =========================================================================
   Warsaken Command — theme.js
   ========================================================================= */
(function () {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const routes = window.routes || {};

  /* Feature-detect iOS (iPhone/iPad/iPod, including iPadOS pretending to be
     Mac Safari). iOS Safari has long-running bugs compositing mask + blend +
     transform stacks used by the foil overlays, so we tag <html> with .is-ios
     and the CSS hides those layers entirely on iPhones. */
  const isIOS =
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) document.documentElement.classList.add('is-ios');

  /* Spinner hide-on-load.
     The .ph::before foil spinner is shown by default whenever a .ph
     contains an <img>. Without an explicit "done" signal it keeps
     animating forever — visible behind any image that has transparent
     pixels. This marks .ph.is-loaded as soon as the contained <img>
     fires load (or errors), so the CSS can switch the spinner off.
     "load" doesn't bubble, so we use capture phase to catch it from
     any descendant. Initial scan catches images that were already
     complete by the time this script ran. */
  function markPhLoaded(img) {
    const ph = img && img.closest && img.closest('.ph');
    if (ph) ph.classList.add('is-loaded');
  }
  document.addEventListener('load', (e) => {
    if (e.target && e.target.tagName === 'IMG') markPhLoaded(e.target);
  }, true);
  document.addEventListener('error', (e) => {
    if (e.target && e.target.tagName === 'IMG') markPhLoaded(e.target);
  }, true);
  document.querySelectorAll('.ph img').forEach(img => {
    if (img.complete && img.naturalWidth > 0) markPhLoaded(img);
  });

  /* Section-cards auto-fit.
     For each [data-section-cards-autofit] section, measure the natural
     width of the track (cards + gaps, BEFORE auto-fit zoom is applied)
     against the PAGE content width — the same cap any .container section
     gets (min(viewport, --maxw) minus the standard 2 * --gutter padding).
     If the track is smaller, set --auto-fit-scale to the ratio so the
     cards scale up exactly to that cap. If equal or larger, the scale
     stays at 1 and the scroller's default horizontal overflow takes over.
     The cap is the page max width even on monitors wider than --maxw,
     so cards never blow up past the rest of the page's content column. */
  (function initSectionCardsAutoFit() {
    function pageContentWidth() {
      const root = document.documentElement;
      const cs = getComputedStyle(root);
      const maxw = parseFloat(cs.getPropertyValue('--maxw')) || 1440;
      const gutter = parseFloat(cs.getPropertyValue('--gutter')) || 32;
      return Math.min(window.innerWidth, maxw) - (2 * gutter);
    }
    function compute(section) {
      const track = section.querySelector('.section-cards-track');
      if (!track) return;
      // Reset so we read the un-auto-scaled natural width.
      section.style.setProperty('--auto-fit-scale', '1');
      requestAnimationFrame(() => {
        const naturalWidth = track.scrollWidth;
        if (naturalWidth <= 0) return;
        const available = pageContentWidth();
        if (available > naturalWidth) {
          section.style.setProperty('--auto-fit-scale', (available / naturalWidth).toFixed(4));
        }
        // else: leave at 1; scroller handles overflow normally.
      });
    }
    const sections = $$('[data-section-cards-autofit]');
    if (!sections.length) return;
    function recompute() { sections.forEach(compute); }
    recompute();
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(recompute, 80);
    });
  })();

  /* Section-card price fetch.
     For each .section-card-price element with a data-section-card-price-fetch
     URL (set by Liquid when the merchant turned on "Show product price" but
     didn't pick the product via the dedicated picker), fetch the product
     via /products/<handle>.js and write the formatted price into the span.
     /products.js handles URL encoding natively, so this works for handles
     containing ®, ™, etc. — which Liquid's all_products[] silently fails on.
     Identical URLs are deduped so 5 cards linking to the same product only
     trigger one network round-trip. */
  (function initSectionCardPriceFetch() {
    const priceCache = new Map();
    function formatPrice(cents, format) {
      if (typeof cents !== 'number' || !isFinite(cents)) return '';
      const fmt = format || '${{amount}}';
      const v = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return fmt.replace(/\{\{\s*amount\s*\}\}/, v);
    }
    function productUrlFromCardUrl(url) {
      // Accepts /products/handle, /products/handle?variant=…,
      // /collections/foo/products/handle, shopify://products/handle, and
      // returns a relative /products/<handle>.js endpoint URL — Shopify
      // resolves URL encoding for us at that endpoint.
      const m = String(url || '').match(/\/products\/([^?#\/]+)/);
      if (!m) return null;
      // m[1] may already be URL-encoded (Shopify's resolved form). Decode
      // first so we re-encode consistently in the final URL.
      let handle;
      try { handle = decodeURIComponent(m[1]); } catch (_) { handle = m[1]; }
      return `/products/${encodeURIComponent(handle)}.js`;
    }
    document.querySelectorAll('[data-section-card-price-fetch]').forEach(el => {
      const url = el.dataset.sectionCardPriceFetch;
      const fmt = el.dataset.moneyFormat || '${{amount}}';
      const endpoint = productUrlFromCardUrl(url);
      if (!endpoint) return;
      let cached = priceCache.get(endpoint);
      if (!cached) {
        cached = fetch(endpoint, { headers: { Accept: 'application/json' } })
          .then(r => (r.ok ? r.json() : null))
          .catch(() => null);
        priceCache.set(endpoint, cached);
      }
      cached.then(product => {
        if (!product) return;
        el.textContent = formatPrice(product.price, fmt);
      });
    });
  })();

  /* Expose the live header height as --header-height on :root, used by the
     Editorial "Tuck under navigation" option to pull a section up behind
     the header. Re-measure on resize to handle responsive header sizes. */
  function measureHeaderHeight() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const h = header.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--header-height', `${h}px`);
  }
  measureHeaderHeight();
  window.addEventListener('resize', measureHeaderHeight);

  /* Tuck-under-nav scroll handler. CSS (via :has()) handles the initial
     inverted state at the top of the page — so the header is already
     transparent on first paint, no JS-triggered flash. This handler only
     toggles html.scrolled-past-tuck once the user has scrolled past
     ~100px, which short-circuits the :has() rule and the header reverts
     to its normal solid styling. */
  const TUCK_SCROLL_THRESHOLD = 100;
  function updateTuckScroll() {
    document.documentElement.classList.toggle(
      'scrolled-past-tuck',
      window.scrollY >= TUCK_SCROLL_THRESHOLD
    );
  }
  window.addEventListener('scroll', updateTuckScroll, { passive: true });
  updateTuckScroll();

  /* Foil animation is now pure CSS — @property --foil-a + @keyframes foil-cycle
     on <html> in base.css (no JS timer, reduced-motion handled via @media). */

  /* -----------------------------------------------------------------------
     Header: hide-on-scroll border + mega menu controller
     ----------------------------------------------------------------------- */
  const header = $('.site-header');
  if (header) {
    let scrolled = false;
    const onScroll = () => {
      const now = window.scrollY > 8;
      if (now !== scrolled) {
        scrolled = now;
        header.classList.toggle('scrolled', scrolled);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* -----------------------------------------------------------------------
     Header WAX wallet indicator — reads warsaken-wax-account from
     localStorage (written by loot-redeem.js). Updates on:
       - page load
       - storage event (linked/unlinked in another tab)
       - warsaken:wax-account CustomEvent (linked/unlinked in this tab)
     ----------------------------------------------------------------------- */
  const walletIndicator = $('[data-wax-actor-indicator]');
  if (walletIndicator) {
    const walletName = walletIndicator.querySelector('[data-wax-actor-name]');
    const syncWallet = (actor) => {
      const value = (actor || '').trim();
      if (value) {
        if (walletName) walletName.textContent = value;
        walletIndicator.hidden = false;
      } else {
        walletIndicator.hidden = true;
        if (walletName) walletName.textContent = '';
      }
    };
    try { syncWallet(localStorage.getItem('warsaken-wax-account')); } catch (_) {}
    window.addEventListener('storage', (e) => {
      if (e.key === 'warsaken-wax-account') syncWallet(e.newValue);
    });
    window.addEventListener('warsaken:wax-account', (e) => {
      syncWallet(e.detail && e.detail.actor);
    });
  }

  /* Mega menu — hover to peek, click to open, second click to navigate */
  const menuPanel = $('.mega-menu');
  const navTriggers = $$('[data-mega-trigger]');
  let activePanel = null;
  let closeTimer = null;
  let menuMode = null; // null | 'hover' | 'click'

  function openPanel(handle) {
    if (!menuPanel) return;
    clearTimeout(closeTimer);
    activePanel = handle;
    $$('.mega-panel', menuPanel).forEach(p => {
      p.classList.toggle('active', p.dataset.handle === handle);
    });
    navTriggers.forEach(t => {
      t.setAttribute('aria-expanded', t.dataset.megaTrigger === handle ? 'true' : 'false');
    });
    menuPanel.classList.add('open');
    header && header.classList.add('menu-open');
  }
  function closePanel() {
    if (!menuPanel) return;
    activePanel = null;
    menuMode = null;
    menuPanel.classList.remove('open');
    header && header.classList.remove('menu-open');
    navTriggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
  }
  function scheduleClose() {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(closePanel, 140);
  }

  navTriggers.forEach(trigger => {
    const handle = trigger.dataset.megaTrigger;
    on(trigger, 'mouseenter', () => {
      if (menuMode !== 'click') {
        menuMode = 'hover';
        openPanel(handle);
      }
    });
    on(trigger, 'focus', () => {
      menuMode = 'hover';
      openPanel(handle);
    });
    on(trigger, 'click', (e) => {
      // Apple-style: first click opens, second navigates
      if (activePanel !== handle) {
        e.preventDefault();
        menuMode = 'click';
        openPanel(handle);
      } else {
        closePanel();
      }
    });
  });

  // Plain primary-nav links (no panel) close any hover-opened panel as the
  // cursor crosses them, so drifting from "Boxed" to "About" doesn't leave
  // the Boxed panel hanging open. Click-opened panels stay (the user is
  // explicit) until the document-click handler dismisses them.
  $$('.primary-nav-link:not([data-mega-trigger])').forEach(link => {
    on(link, 'mouseenter', () => {
      if (menuMode === 'hover') closePanel();
    });
  });
  on(header, 'mouseleave', () => {
    if (menuMode === 'hover') scheduleClose();
  });
  on(menuPanel, 'mouseenter', () => clearTimeout(closeTimer));
  on(menuPanel, 'mouseleave', () => {
    if (menuMode === 'hover') scheduleClose();
  });
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') closePanel();
  });
  on(document, 'click', (e) => {
    if (!menuPanel || !header) return;
    if (!header.contains(e.target)) closePanel();
  });

  /* -----------------------------------------------------------------------
     Region / currency dropdown
     ----------------------------------------------------------------------- */
  const regionSelector = $('[data-region-selector]');
  const regionToggle = $('[data-region-toggle]');
  const regionDropdown = $('[data-region-dropdown]');
  function setRegionOpen(open) {
    if (!regionDropdown || !regionToggle) return;
    if (open) {
      regionDropdown.setAttribute('data-open', '');
      regionToggle.setAttribute('aria-expanded', 'true');
    } else {
      regionDropdown.removeAttribute('data-open');
      regionToggle.setAttribute('aria-expanded', 'false');
    }
  }
  on(regionToggle, 'click', (e) => {
    e.stopPropagation();
    const open = regionDropdown.hasAttribute('data-open');
    setRegionOpen(!open);
  });
  on(document, 'click', (e) => {
    if (regionSelector && !regionSelector.contains(e.target)) setRegionOpen(false);
  });
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape') setRegionOpen(false);
  });

  /* -----------------------------------------------------------------------
     Mobile menu
     ----------------------------------------------------------------------- */
  const mobileMenu = $('.mobile-menu');
  const mobileOpenBtn = $('[data-mobile-open]');
  const mobileCloseBtn = $('[data-mobile-close]');
  function setMobile(open) {
    if (!mobileMenu) return;
    mobileMenu.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.style.overflow = open ? 'hidden' : '';
  }
  on(mobileOpenBtn, 'click', () => setMobile(true));
  on(mobileCloseBtn, 'click', () => setMobile(false));

  /* Close every open nav surface when the viewport width changes. A width
     change usually means a breakpoint crossing (desktop mega menu <->
     mobile drawer), and a panel / drawer / accordion left open from the
     old layout looks broken in the new one. We gate on WIDTH only - mobile
     browsers fire resize when the URL bar shows/hides during scroll, which
     changes height but not width, and we don't want that to slam menus
     shut. Debounced so it runs once after the resize settles. */
  let navLastWidth = window.innerWidth;
  let navResizeTimer = null;
  window.addEventListener('resize', () => {
    if (window.innerWidth === navLastWidth) return;
    navLastWidth = window.innerWidth;
    clearTimeout(navResizeTimer);
    navResizeTimer = setTimeout(() => {
      closePanel();      // desktop mega menu panel
      setMobile(false);  // mobile drawer (also unlocks body scroll)
      // Mobile mega-menu accordions: snap closed instantly (no animation
      // needed mid-resize) and clear any inline max-height the open/close
      // transition may have left behind.
      $$('.mobile-mega-item[open]').forEach((d) => {
        d.open = false;
        const panel = d.querySelector('.mobile-mega-panel');
        if (panel) panel.style.maxHeight = '';
      });
    }, 100);
  });

  /* -----------------------------------------------------------------------
     Search drawer
     ----------------------------------------------------------------------- */
  const searchDrawer = $('.search-drawer');
  const searchScrim = $('.search-scrim');
  const searchTrigger = $('[data-search-trigger]');
  const searchCloseTriggers = $$('[data-search-close]');
  function setSearch(open) {
    if (!searchDrawer) return;
    searchDrawer.classList.toggle('open', open);
    if (searchScrim) searchScrim.classList.toggle('open', open);
    if (open) {
      setTimeout(() => $('input', searchDrawer)?.focus(), 80);
    }
  }
  on(searchTrigger, 'click', () => setSearch(true));
  searchCloseTriggers.forEach((b) => on(b, 'click', () => setSearch(false)));
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape' && searchDrawer?.classList.contains('open')) setSearch(false);
  });

  /* -----------------------------------------------------------------------
     Cart drawer
     ----------------------------------------------------------------------- */
  const cartScrim = $('.cart-scrim');
  const cartDrawer = $('.cart-drawer');
  const cartOpenTriggers = $$('[data-cart-open]');
  const cartCloseTriggers = $$('[data-cart-close]');
  const cartCountEls = $$('[data-cart-count]');

  function openCart() {
    if (!cartDrawer) return;
    cartScrim?.classList.add('open');
    cartDrawer.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function closeCart() {
    cartScrim?.classList.remove('open');
    cartDrawer?.classList.remove('open');
    document.body.style.overflow = '';
  }
  cartOpenTriggers.forEach(b => on(b, 'click', (e) => { e.preventDefault(); openCart(); }));
  cartCloseTriggers.forEach(b => on(b, 'click', closeCart));

  function setCartCount(count) {
    cartCountEls.forEach(el => { el.textContent = String(count); });
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  // Capture errors for initial markup and subsequent drawer section swaps.
  function deckbookPreviewFailed(image) {
    if (!image.matches?.('[data-deckbook-preview]')) return;
    image.parentElement?.querySelectorAll('[data-deckbook-image-fallback]').forEach(fallback => { fallback.hidden = false; });
    image.remove();
  }
  function initDeckbookPreviews(root = document) {
    root.querySelectorAll('[data-deckbook-preview]').forEach(image => {
      if (image.complete && image.naturalWidth === 0) deckbookPreviewFailed(image);
    });
  }
  document.addEventListener('error', event => deckbookPreviewFailed(event.target), true);
  initDeckbookPreviews();

  async function refreshCartDrawer() {
    if (!cartDrawer) return;
    try {
      // Fire both fetches in parallel and let /cart.js win the race — it
      // typically returns before the section render, so the list view + cart
      // count update without waiting for the drawer body.
      const cartPromise = fetchCart();
      const htmlPromise = fetch(`${routes.cart_url || '/cart'}?section_id=cart-drawer`).then(r => r.text());

      cartPromise.then(cart => {
        setCartCount(cart.item_count);
        syncListView(cart);
      }).catch(() => {});

      const [html] = await Promise.all([htmlPromise]);
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-cart-drawer-content]');
      const current = $('[data-cart-drawer-content]', cartDrawer);
      if (next && current) {
        current.innerHTML = next.innerHTML;
        initDeckbookPreviews(current);
        attachCartItemHandlers();
      } else {
        console.warn('[cart-drawer] swap skipped — next:', !!next, 'current:', !!current, 'first 200 chars of response:', (html || '').slice(0, 200));
      }
    } catch (e) { console.error('[cart-drawer] refresh failed', e); }
    // Signal derived UI (cashback amount, anything else watching cart state)
    // that the drawer body just got fresh markup. Past-tense event name so
    // it's distinct from warsaken:cart-refresh which REQUESTS a refresh.
    try { window.dispatchEvent(new CustomEvent('warsaken:cart-changed')); } catch (_) {}
  }
  // Cross-module hook: anyone (e.g. loot-redeem.js after an auto-apply)
  // can dispatch warsaken:cart-refresh on window to trigger a fresh
  // /cart.js + drawer re-render. Use this when something updated the
  // cart cookie state outside of this module's own AJAX handlers.
  window.addEventListener('warsaken:cart-refresh', () => { refreshCartDrawer(); });

  /* Saved DeckBooks enter the existing first-party Shopify cart. */
  function deckbookConfiguratorUrl(element) {
    return new URL(element.dataset.deckbookLocalTesting === 'true' ? 'http://localhost:1983' : element.dataset.deckbookConfiguratorUrl);
  }
  function deckbookConfiguratorUrlAllowed(url, localTesting) {
    return !url.username && !url.password && (url.protocol === 'https:' ||
      (localTesting === 'true' && url.protocol === 'http:' &&
        ['localhost', '127.0.0.1'].includes(url.hostname) && url.port === '1983'));
  }
  function initDeckbookEditLinks(root = document) {
    $$('[data-deckbook-edit-url]', root).forEach(link => {
      link.hidden = true;
      try {
        const approved = deckbookConfiguratorUrl(link);
        const target = new URL(link.dataset.deckbookEditUrl);
        const id = link.dataset.deckbookDesignId;
        if (!deckbookConfiguratorUrlAllowed(target, link.dataset.deckbookLocalTesting) || target.origin !== approved.origin || target.pathname !== approved.pathname ||
            !/^[0-9a-f-]{36}$/i.test(id)) return;
        if (link.dataset.deckbookEditReplace === 'true') target.searchParams.set('deckbookReplace', id);
        else target.searchParams.delete('deckbookReplace');
        link.href = target.href; link.hidden = false;
      } catch {}
    });
  }
  initDeckbookEditLinks();
  window.addEventListener('warsaken:cart-changed', () => initDeckbookEditLinks());
  document.addEventListener('shopify:section:load', event => initDeckbookEditLinks(event.target));

  (function initDeckbookHandoff() {
    const page = document.querySelector('[data-deckbook-handoff]');
    if (!page) return;
    const rootUrl = new URL(window.Shopify?.routes?.root || '/', location.origin);
    const status = page.querySelector('[data-deckbook-handoff-status]');
    const title = page.querySelector('[data-deckbook-handoff-title]');
    const retry = page.querySelector('[data-deckbook-handoff-retry]');
    const addNew = page.querySelector('[data-deckbook-handoff-add-new]');
    const back = page.querySelector('[data-deckbook-handoff-back]');
    const storageKey = 'warsaken:deckbook-handoff:v1';
    const incoming = window.__deckbookIncoming || {};
    delete window.__deckbookIncoming;
    let state = {}, busy = false, allowNew = false;
    try {
      const approved = deckbookConfiguratorUrl(page);
      if (deckbookConfiguratorUrlAllowed(approved, page.dataset.deckbookLocalTesting)) { back.href = approved.href; back.hidden = false; }
    } catch {}
    const save = () => { try { sessionStorage.setItem(storageKey, JSON.stringify(state)); } catch {} };
    const uuid = value => /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value || '');
    function decode(packet) {
      if (packet.length > 22000 || !/^[a-zA-Z0-9_-]+$/.test(packet)) throw new Error('The design link is invalid. Please return to the configurator.');
      const binary = atob(packet.replace(/-/g, '+').replace(/_/g, '/'));
      if (binary.length > 16384) throw new Error('The design link is too large. Please use the configurator’s shop connection.');
      const parsed = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(binary, char => char.charCodeAt(0))));
      if (!parsed || parsed.schemaVersion !== 1 || parsed.product !== 'eclectic-nerds-deckbook' || parsed.artworkApproved !== true || !parsed.configuration || !parsed.pricing) throw new Error('The design link is incomplete. Please review the design again.');
      return Object.fromEntries(['schemaVersion', 'product', 'configuration', 'pricing', 'artworkApproved', 'customColorAcknowledgment', 'replacesDesignId'].filter(key => parsed[key] !== undefined).map(key => [key, parsed[key]]));
    }
    async function json(url, options = {}) {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', ...options,
          headers: { Accept: 'application/json', ...(options.body ? { 'Content-Type': 'application/json' } : {}), ...options.headers }, signal: controller.signal });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error?.message || data.description || 'The shop could not update your loadout. Please try again.');
        return data;
      } finally { clearTimeout(timer); }
    }
    const cart = () => json(new URL('cart.js', rootUrl));
    const postCart = (path, data) => json(new URL(path, rootUrl), { method: 'POST', body: JSON.stringify(data) });
    function findDesign(current, id) {
      const lines = (current.items || []).filter(line => line.properties?._deckbook_design_id === id);
      if (lines.length > 1) throw new Error('This design appears more than once. Please review your loadout before continuing.');
      return lines[0];
    }
    function validate(h) {
      if (h.schemaVersion !== 1 || !uuid(h.designId) || !Number.isFinite(Date.parse(h.expiresAt)) || Date.parse(h.expiresAt) <= Date.now() ||
          !/^\d+$/.test(String(h.line?.variantId)) || h.line.quantity !== 1 ||
          h.line.properties?._deckbook_design_id !== h.designId || !/^[a-f0-9]{64}$/.test(h.line.properties?._deckbook_signature || '') ||
          (h.replacesDesignId && (!uuid(h.replacesDesignId) || h.replacesDesignId === h.designId)) ||
          !Object.values(h.line.properties || {}).every(value => typeof value === 'string' && value.length <= 4000)) throw new Error('The saved design could not be verified. Return to the configurator and try again.');
      try {
        const designUrl = new URL(h.line.properties._deckbook_edit_url);
        const approved = deckbookConfiguratorUrl(page);
        if (deckbookConfiguratorUrlAllowed(designUrl, page.dataset.deckbookLocalTesting) && designUrl.origin === approved.origin && designUrl.pathname === approved.pathname) {
          back.href = designUrl.href; back.textContent = 'Return to your saved design'; back.hidden = false;
        }
      } catch {}
    }
    function verifyExisting(line, h) {
      if (String(line.variant_id) !== String(h.line.variantId) || !Object.entries(h.line.properties).every(([key, value]) => line.properties?.[key] === value)) throw new Error('Your loadout has a different version of this design. Please review it before continuing.');
    }
    async function mutate(h) {
      let current = await cart(), present = findDesign(current, h.designId);
      const original = h.replacesDesignId ? findDesign(current, h.replacesDesignId) : null;
      if (present) verifyExisting(present, h);
      let previouslyAdded = false;
      try { previouslyAdded = localStorage.getItem('warsaken:deckbook-added:' + h.designId) === 'true'; } catch {}
      if (!present && previouslyAdded && !allowNew) {
        title.textContent = 'This DeckBook was already added';
        status.textContent = 'This design is no longer in your loadout. Add it again as a new item?';
        addNew.hidden = false;
        return false;
      }
      if (h.replacesDesignId && !original && !present && !allowNew) {
        title.textContent = 'Original DeckBook not found';
        status.textContent = 'The original design is no longer in this loadout. Add your edited design as a new item?';
        addNew.hidden = false;
        return false;
      }
      const quantity = original?.quantity || 1;
      if (!present) {
        // Rechecking the cart after an ambiguous timeout is essential: the
        // server may already have added the book even when its reply was lost.
        try { await postCart('cart/add.js', { items: [{ id: h.line.variantId, quantity, properties: h.line.properties }] }); }
        catch (error) {
          current = await cart(); present = findDesign(current, h.designId);
          if (!present) throw error;
          verifyExisting(present, h);
        }
        current = await cart(); present = findDesign(current, h.designId);
        if (!present) throw new Error('The shop has not confirmed the add yet. Try again; your original items are safe.');
        verifyExisting(present, h);
      }
      if (original) {
        if (present.quantity !== quantity) await postCart('cart/change.js', { id: present.key, quantity });
        // Refresh keys because Shopify may change them after promotions.
        current = await cart();
        const oldLine = findDesign(current, h.replacesDesignId);
        if (oldLine) {
          if (oldLine.quantity !== quantity) throw new Error('The original book’s quantity changed during the edit. Try again to preserve the current quantity.');
          try { await postCart('cart/change.js', { id: oldLine.key, quantity: 0 }); }
          catch {
            const verified = await cart();
            if (findDesign(verified, h.replacesDesignId)) throw new Error('Your edited book was added, but the original could not be removed. Try again to finish the replacement; another copy will not be added.');
          }
        }
      }
      // Returning through browser history must not undo a deliberate removal
      // or refill a cart after checkout. A new add requires an explicit choice.
      try { localStorage.setItem('warsaken:deckbook-added:' + h.designId, 'true'); } catch {}
      return true;
    }
    async function run() {
      if (busy) return;
      busy = true; retry.hidden = true; addNew.hidden = true;
      title.textContent = 'Adding your DeckBook…';
      status.textContent = 'Adding your approved design to your existing loadout.';
      try {
        const apiBase = new URL('https://n8n.warsaken.io/webhook/');
        if (state.packet && !state.token) {
          const accepted = await json(new URL('deckbook-configurations', apiBase), { method: 'POST', headers: { 'Idempotency-Key': state.idempotencyKey }, body: JSON.stringify(state.packet) });
          const destination = new URL(accepted.checkoutUrl);
          if (destination.origin !== 'https://shop.warsaken.com' || destination.pathname !== '/pages/deckbook-handoff' || !/^[a-zA-Z0-9_-]{43}$/.test(destination.searchParams.get('token') || '')) throw new Error('The shop returned an invalid handoff. Please try again.');
          state.token = destination.searchParams.get('token'); delete state.packet; save();
        }
        if (!/^[a-zA-Z0-9_-]{43}$/.test(state.token || '')) throw new Error('Return to your DeckBook design and select Continue to shop.');
        const resolver = new URL('deckbook-handoff', apiBase);
        resolver.searchParams.set('token', state.token);
        const h = await json(resolver);
        validate(h);
        if (!navigator.locks?.request) throw new Error('Please use an updated browser to add your DeckBook safely.');
        // Serializes additions across tabs, not just this button's busy flag.
        const completed = await navigator.locks.request('warsaken-deckbook-cart', () => mutate(h));
        if (completed) {
          title.textContent = 'Your DeckBook is in your loadout';
          status.textContent = 'Opening your loadout…';
          location.assign(new URL('cart', rootUrl).href);
        }
      } catch (error) {
        title.textContent = 'Your DeckBook needs another try';
        status.textContent = error.name === 'AbortError' ? 'The shop took too long to respond. Try again; we will check your loadout before adding anything.' : error.message;
        retry.hidden = false;
      } finally { busy = false; }
    }
    retry.addEventListener('click', run);
    addNew.addEventListener('click', () => { allowNew = true; run(); });
    try {
      if (incoming.packet !== null && incoming.packet !== undefined) {
        state = { packet: decode(incoming.packet), idempotencyKey: crypto.randomUUID() }; save();
      } else if (incoming.token) { state = { token: incoming.token }; save(); }
      else { try { state = JSON.parse(sessionStorage.getItem(storageKey) || '{}'); } catch {} }
      run();
    } catch (error) { title.textContent = 'This design link could not be opened'; status.textContent = error.message; }
  })();

  // Re-render every [data-list-action] wrapper from the actual cart state so
  // the list view never drifts from the server. Called after every cart
  // mutation (via refreshCartDrawer) and in catch handlers when an optimistic
  // update fails. Accepts an optional pre-fetched cart object to avoid a
  // duplicate /cart.js round-trip.
  async function syncListView(cartObj) {
    // Was using document.querySelector — only the FIRST [data-view-list]
    // got synced. On singles collections we render two list containers
    // (filter-results + standard), and items added by any path other than
    // the list view's own controls (PDP submit, drawer +/-, quick-add)
    // would refresh the first list but leave the second stale.
    const listRoots = document.querySelectorAll('[data-view-list]');
    if (!listRoots.length) return;
    try {
      const cart = cartObj || await fetchCart();
      const qtyByVariant = {};
      (cart.items || []).forEach(item => { qtyByVariant[item.variant_id] = item.quantity; });
      listRoots.forEach((listRoot) => {
        listRoot.querySelectorAll('[data-list-action]').forEach(action => {
          const variantId = action.dataset.variantId;
          if (!variantId) return;
          const qty = qtyByVariant[variantId] || 0;
          const numEl = action.querySelector('.list-qty-num');
          const hasAdd = !!action.querySelector('[data-list-add]');
          const shownQty = numEl ? parseInt(numEl.textContent, 10) : 0;
          if (qty > 0) {
            if (!numEl || shownQty !== qty) {
              action.innerHTML = listRowQtyHtml(variantId, qty);
            }
          } else if (!hasAdd) {
            action.innerHTML = listRowAddHtml(variantId);
          }
        });
      });
    } catch (e) { /* ignore */ }
  }

  async function addToCart(formOrItems, options = {}) {
    let body, headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (formOrItems instanceof FormData) {
      body = formOrItems;
      headers = { Accept: 'application/json' };
    } else {
      body = JSON.stringify({ items: Array.isArray(formOrItems) ? formOrItems : [formOrItems] });
    }
    const res = await fetch(routes.cart_add_url || '/cart/add.js', { method: 'POST', headers, body });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw err;
    }
    // Default flow blocks on the cart drawer refresh so it's accurate when
    // openCart() runs. Background callers (list view) pass refreshDrawer: false
    // and trigger refreshCartDrawer() themselves without awaiting.
    if (options.refreshDrawer !== false) {
      await refreshCartDrawer();
    }
    if (options.openDrawer !== false) openCart();
    return res.json();
  }

  async function clearCart() {
    // Optimistic: paint the cleared state instantly, before the round-trip.
    // The list view and header count update right away; the drawer body
    // catches up when refreshCartDrawer's render returns.
    setCartCount(0);
    syncListView({ items: [] });

    try {
      const res = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) throw await res.json().catch(() => ({}));
      refreshCartDrawer();  // background — re-renders drawer body
      return res.json();
    } catch (err) {
      // Request failed — re-sync from actual server state.
      refreshCartDrawer();
      throw err;
    }
  }

  async function changeCart(id, quantity) {
    // Optimistic: paint the qty change on the user's row INSTANTLY,
    // before the AJAX round-trip. The section refresh that follows
    // overwrites the section with authoritative server state, so a
    // brief mismatch is fine and goes away in <300ms.
    applyOptimisticCartLineUpdate(id, quantity);

    const res = await fetch(routes.cart_change_url || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id, quantity }),
    });
    if (!res.ok) throw await res.json().catch(() => ({}));
    // Route the post-change refresh by surface. The drawer's irrelevant
    // on /cart (and vice versa); skipping the wrong-surface fetch keeps
    // both paths snappy.
    if (document.body.classList.contains('template-cart')) {
      await refreshCartPage();
    } else {
      await refreshCartDrawer();
    }
    return res.json();
  }

  // Optimistic per-row update on the cart page / drawer — used by changeCart
  // to give instant click feedback. Targets every button matching the line
  // key in both surfaces; the trailing refresh swaps the whole section so
  // any drift fixes itself.
  function applyOptimisticCartLineUpdate(key, qty) {
    if (!key) return;
    const sel = CSS.escape ? CSS.escape(key) : key.replace(/"/g, '\\"');
    const incBtns = document.querySelectorAll('[data-cart-qty-inc="' + sel + '"]');
    const decBtns = document.querySelectorAll('[data-cart-qty-dec="' + sel + '"]');
    if (qty <= 0) {
      // Soft-hide the row so the user sees the removal land before the
      // section refresh removes the markup outright.
      incBtns.forEach(btn => {
        const row = btn.closest('.cart-item, li.cart-item');
        if (row) { row.style.opacity = '0.35'; row.style.pointerEvents = 'none'; }
      });
      return;
    }
    incBtns.forEach(btn => { btn.dataset.qty = String(qty); });
    decBtns.forEach(btn => { btn.dataset.qty = String(qty); });
    // The visible qty number sits as the sibling .num between the two buttons.
    incBtns.forEach(btn => {
      const numEl = btn.parentElement && btn.parentElement.querySelector('.num');
      if (numEl) numEl.textContent = String(qty);
    });
  }

  // Partial refresh of the /cart page — fetches the section by id and
  // swaps the live section's innerHTML with the freshly-rendered markup.
  // Avoids the full-page reload that was making qty +/- feel sluggish.
  // Same shape as refreshCartDrawer, just targets the cart page section.
  async function refreshCartPage() {
    // Pragmatic — the section-id partial swap proved unreliable (Shopify
    // sometimes returned stale section markup, leaving totals/cashback out
    // of sync with the just-committed cart). A full reload guarantees the
    // page reflects authoritative state. Optimistic UI in applyOptimistic
    // CartLineUpdate already painted the qty change instantly so the
    // reload reads as confirmation, not as "did anything happen?"
    window.location.reload();
  }

  // Targeted optimistic update for a single list-view row by variant id.
  // Cheaper than syncListView (no /cart.js round-trip, no full iteration).
  // Updates the row in EVERY list container — same multi-container reason
  // as syncListView (singles collections render two of them).
  function optimisticListUpdate(variantId, qty) {
    if (!variantId) return;
    const actions = document.querySelectorAll('[data-view-list] [data-list-action][data-variant-id="' + variantId + '"]');
    actions.forEach((action) => {
      if (qty <= 0) {
        if (!action.querySelector('[data-list-add]')) {
          action.innerHTML = listRowAddHtml(variantId);
        }
      } else {
        const numEl = action.querySelector('.list-qty-num');
        if (!numEl) {
          action.innerHTML = listRowQtyHtml(variantId, qty);
        } else if (parseInt(numEl.textContent, 10) !== qty) {
          numEl.textContent = String(qty);
          action.querySelectorAll('[data-qty]').forEach(b => { b.dataset.qty = qty; });
        }
      }
    });
  }

  // Cart inc / dec / remove — single document-level delegate so both the
  // slide-out drawer AND the full cart page wire up. The drawer body gets
  // swapped on every refresh (new buttons each time) and the cart page's
  // buttons exist at page-load; per-button attachment can't cover both
  // without re-running on every refresh, so we delegate once and never
  // worry about it again.
  on(document, 'click', (e) => {
    const incBtn = e.target.closest('[data-cart-qty-inc]');
    if (incBtn) {
      const key = incBtn.dataset.cartQtyInc;
      const variantId = incBtn.dataset.variantId;
      const qty = parseInt(incBtn.dataset.qty || '0', 10) + 1;
      optimisticListUpdate(variantId, qty);
      changeCart(key, qty).catch(() => syncListView());
      return;
    }
    const decBtn = e.target.closest('[data-cart-qty-dec]');
    if (decBtn) {
      const key = decBtn.dataset.cartQtyDec;
      const variantId = decBtn.dataset.variantId;
      const qty = Math.max(0, parseInt(decBtn.dataset.qty || '0', 10) - 1);
      optimisticListUpdate(variantId, qty);
      changeCart(key, qty).catch(() => syncListView());
      return;
    }
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      const key = removeBtn.dataset.cartRemove;
      const variantId = removeBtn.dataset.variantId;
      optimisticListUpdate(variantId, 0);
      changeCart(key, 0).catch(() => syncListView());
      return;
    }
  });
  // Kept as a no-op for places that still call into the old function.
  // refreshCartDrawer used to re-attach per-button handlers after each
  // body swap; with document delegation that's no longer required.
  function attachCartItemHandlers() {}

  // Cart clear — delegated at document level so both the drawer's
  // "Clear all" and the cart-page header button hit the same path.
  // After clearing on /cart, full reload so Shopify renders the empty
  // state markup (the page layout differs entirely between empty and
  // non-empty); inside the drawer, refreshCartDrawer already re-paints.
  // Buy item anchor — fixed bottom widget. Adds the configured variant
  // to the cart, then navigates to /cart. No drawer open / no drawer
  // refresh on this path since we're about to leave the page; the cart
  // page will pick up the fresh state on its next render.
  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-buy-anchor-buy]');
    if (!btn) return;
    if (btn.disabled) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding…';
    addToCart([{ id: Number(variantId), quantity: 1 }], { refreshDrawer: false, openDrawer: false })
      .then(() => { window.location.href = routes.cart_url || '/cart'; })
      .catch((err) => {
        console.warn('[buy-anchor] add failed', err);
        btn.textContent = originalText;
        btn.disabled = false;
      });
  });

  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-cart-clear]');
    if (!btn) return;
    clearCart().then(() => {
      // /cart renders item layout vs. empty layout from the section
      // template; the section-id fetch in refreshCartPage swaps the
      // whole section so the empty-state markup comes in correctly
      // without a full page reload.
      if (document.body.classList.contains('template-cart')) {
        refreshCartPage();
      }
    }).catch(() => {});
  });

  /* Cart-page discount code form. Hits /discount/<code>?redirect=/cart
     which sets Shopify's discount cookie and bounces back to the cart
     page where discount_applications will pick up the new code (or
     ignore it if invalid). Same pattern as loot-redeem.js applying
     issued Loot codes. */
  /* The discount container is a <div>, not a <form>, because the cart-
     page outer <form> wraps the entire aside (it carries the Checkout
     submit button). Nested forms are invalid HTML - the browser drops
     the inner one - so we can't rely on a submit event here. Listen on
     the apply button click + the input's Enter keydown instead. */
  function applyCartDiscount(container) {
    const input = container.querySelector('[data-cart-discount-input]');
    const code = (input && input.value || '').trim();
    if (!code) {
      if (input) input.focus();
      return;
    }
    const btn = container.querySelector('[data-cart-discount-apply]');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Applying…';
    }
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart');
  }
  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-cart-discount-apply]');
    if (!btn) return;
    e.preventDefault();
    const container = btn.closest('[data-cart-discount-form]');
    if (container) applyCartDiscount(container);
  });
  on(document, 'keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('[data-cart-discount-input]');
    if (!input) return;
    e.preventDefault();
    const container = input.closest('[data-cart-discount-form]');
    if (container) applyCartDiscount(container);
  });

  /* Cart-page per-code removal. POST /cart/update.js with the new
     discount_codes array (current codes minus the one clicked). The
     array is sourced from the DOM (data-cart-discount-remove on every
     chip), so this handler doesn't need to know about discount state -
     just re-sends whatever the page already shows minus the removed
     entry. Shopify accepts discount_codes as a plain array. */
  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-cart-discount-remove]');
    if (!btn) return;
    e.preventDefault();
    const codeToRemove = btn.getAttribute('data-cart-discount-remove');
    const all = Array.from(document.querySelectorAll('[data-cart-discount-remove]'))
      .map((b) => b.getAttribute('data-cart-discount-remove'))
      .filter((c) => c && c !== codeToRemove);
    btn.disabled = true;
    btn.textContent = '…';
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount_codes: all }),
    })
      .then(() => {
        if (document.body.classList.contains('template-cart')) {
          window.location.reload();
        }
      })
      .catch(() => {
        btn.disabled = false;
        btn.textContent = '×';
      });
  });

  /* -----------------------------------------------------------------------
     Loot back cashback block (cart drawer + cart page)
     - Reads eligible-cents from the rendered .cart-cashback block (Liquid
       already filtered out cashback-excluded + gift card lines).
     - Pulls the live LOOT price and event rate (refreshes every 30 sec),
       floored at $0.00001/LOOT to match the redemption pricing rules.
     - 0.5% of eligible total × event rate → LOOT at floored price → rounded to the
       nearest 500 (never below 500 once any eligible cents exist).
     - Wallet state pulls from localStorage.warsaken-wax-account; the same
       value gets mirrored to the cart attribute "loot_back_wallet" via
       /cart/update.js so the fulfillment workflow can read it off the
       resulting order's note_attributes.
     ----------------------------------------------------------------------- */
  const LOOT_PRICE_FLOOR_USD = 0.00001;
  const LOOT_PRICE_URL = 'https://n8n.warsaken.io/webhook/loot-price';
  const LOOT_PRICE_CACHE_MS = 5 * 60 * 1000;
  let lootBackState = null;
  let lootPriceRequest = null;

  function activeLootBackMultiplier() {
    const rate = Number(lootBackState?.multiplier);
    const now = Date.now();
    return lootBackState?.event_key && Number.isFinite(rate) && rate > 0 && rate <= 100 &&
      Date.parse(lootBackState.starts_at || '') <= now && Date.parse(lootBackState.ends_at || '') > now ? rate : 1;
  }

  async function getLootPriceUsd() {
    try {
      const cached = JSON.parse(sessionStorage.getItem('warsaken-loot-price') || '{}');
      // Price can be cached, but event rates must refresh around start/end.
      if (cached.price && Object.prototype.hasOwnProperty.call(cached, 'lootBack') && Date.now() - cached.t < 30000) {
        lootBackState = cached.lootBack;
        return Math.max(Number(cached.price), LOOT_PRICE_FLOOR_USD);
      }
    } catch (_) {}
    try {
      const r = await fetch(LOOT_PRICE_URL, { cache: 'no-store' });
      if (!r.ok) throw new Error('price feed unavailable');
      const j = await r.json();
      const row = Array.isArray(j) ? j[0] : j;
      const p = Number(row?.latest?.usd ?? row?.usd);
      if (!Number.isFinite(p) || p <= 0) throw new Error('bad price');
      lootBackState = row.loot_back || null;
      try {
        sessionStorage.setItem('warsaken-loot-price', JSON.stringify({ price: p, lootBack: lootBackState, t: Date.now() }));
      } catch (_) {}
      return Math.max(p, LOOT_PRICE_FLOOR_USD);
    } catch (e) {
      console.warn('[cashback] loot price fetch failed', e);
      lootBackState = null;
      // Expired event rates are neutral; use the last known token price if
      // the feed is temporarily unavailable rather than inventing a dip.
      try {
        const cached = JSON.parse(sessionStorage.getItem('warsaken-loot-price') || '{}');
        if (cached.price && Date.now() - cached.t < LOOT_PRICE_CACHE_MS) return Math.max(Number(cached.price), LOOT_PRICE_FLOOR_USD);
      } catch (_) {}
      return LOOT_PRICE_FLOOR_USD;
    }
  }

  function renderCashbackWalletRow(block) {
    const row = block.querySelector('[data-cashback-wallet-row]');
    if (!row) return;
    const wallet = (localStorage.getItem('warsaken-wax-account') || '').trim();
    if (wallet) {
      row.innerHTML =
        'Send to <strong>' + wallet + '</strong> ' +
        '<button type="button" class="cart-cashback__edit" data-cashback-edit>Edit</button>';
      // Sync wallet → cart attribute so the order carries it.
      saveCashbackWalletAttribute(wallet);
    } else {
      row.innerHTML =
        '<button type="button" class="cart-cashback__set" data-cashback-set>Set a destination wallet</button>';
      // Clear any stale attribute on the cart if user removed their wallet.
      saveCashbackWalletAttribute('');
    }
  }

  function openCashbackInput(block) {
    const row = block.querySelector('[data-cashback-wallet-row]');
    if (!row) return;
    const current = (localStorage.getItem('warsaken-wax-account') || '').trim();
    // Input + Save sit inside a shared .field-group (unified pill, no
    // gap between them); Cancel is a separate sibling button. The outer
    // .cart-cashback__edit-row is a flex row so they line up beside
    // each other within .cart-cashback__wallet.
    row.innerHTML =
      '<div class="cart-cashback__edit-row">' +
        '<div class="field-group">' +
          '<input type="text" class="cart-cashback__input" data-cashback-input ' +
          'value="' + current + '" placeholder="yourname.wam" autocomplete="off" ' +
          'autocapitalize="none" spellcheck="false" maxlength="12">' +
          '<button type="button" class="cart-cashback__save" data-cashback-save>Save</button>' +
        '</div>' +
        '<button type="button" class="cart-cashback__cancel" data-cashback-cancel>Cancel</button>' +
      '</div>';
    const input = row.querySelector('[data-cashback-input]');
    input.focus();
    input.select();
  }

  let cashbackAttrInFlight = '';
  async function saveCashbackWalletAttribute(wallet) {
    if (cashbackAttrInFlight === wallet) return;
    cashbackAttrInFlight = wallet;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { loot_back_wallet: wallet } })
      });
    } catch (e) {
      console.warn('[cashback] cart attr save failed', e);
    } finally {
      cashbackAttrInFlight = '';
    }
  }

  async function refreshCashbackBlocks({ refreshWallet = true } = {}) {
    const blocks = $$('[data-cart-cashback]');
    if (!blocks.length) return;
    // Render the wallet row synchronously so the user sees their saved
    // wallet (or the Set link) without waiting on the price fetch.
    if (refreshWallet) blocks.forEach(renderCashbackWalletRow);
    if (!lootPriceRequest) lootPriceRequest = getLootPriceUsd().finally(() => { lootPriceRequest = null; });
    const priceUsd = await lootPriceRequest;
    const multiplier = activeLootBackMultiplier();
    blocks.forEach(block => {
      const eligibleCents = Number(block.dataset.eligibleCents || 0);
      if (eligibleCents <= 0) { block.hidden = true; return; }
      const cashbackUsd = (eligibleCents / 100) * 0.005 * multiplier;
      const lootRaw = cashbackUsd / priceUsd;
      // Round to nearest 500, but never less than 500 once any eligible
      // cents exist (the cashback is meaningless below that floor).
      const loot = Math.max(500, Math.round(lootRaw / 500) * 500);
      const amountEl = block.querySelector('[data-cart-cashback-amount]');
      if (amountEl) amountEl.textContent = loot.toLocaleString();
      const title = block.querySelector('.cart-cashback__title');
      if (title) title.textContent = multiplier === 1 ? 'Loot back reward on this purchase' : `${multiplier}× Loot back reward on this purchase`;
      block.hidden = false;
    });
  }
  refreshCashbackBlocks();
  // Refresh whenever cart state changes — refreshCartDrawer dispatches
  // warsaken:cart-changed after each body swap so the new block (which
  // has fresh data-eligible-cents from liquid) gets its amount computed.
  window.addEventListener('warsaken:cart-changed', () => { refreshCashbackBlocks(); });
  setInterval(() => { if (!document.hidden) refreshCashbackBlocks({ refreshWallet: false }); }, 30000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshCashbackBlocks({ refreshWallet: false }); });
  // Wallet linked / unlinked elsewhere on the page (loot page, header).
  window.addEventListener('warsaken:wax-account', () => {
    $$('[data-cart-cashback]').forEach(renderCashbackWalletRow);
  });
  window.addEventListener('storage', (e) => {
    if (e.key === 'warsaken-wax-account') {
      $$('[data-cart-cashback]').forEach(renderCashbackWalletRow);
    }
  });

  // Edit / Set click → open inline input.
  on(document, 'click', (e) => {
    const trigger = e.target.closest('[data-cashback-edit], [data-cashback-set]');
    if (!trigger) return;
    const block = trigger.closest('[data-cart-cashback]');
    if (block) openCashbackInput(block);
  });
  // Save → persist + dispatch shared event so the header pill updates too.
  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-cashback-save]');
    if (!btn) return;
    const block = btn.closest('[data-cart-cashback]');
    const input = block.querySelector('[data-cashback-input]');
    const v = (input.value || '').trim().toLowerCase();
    if (!/^[a-z1-5.]{1,12}$/.test(v)) {
      input.classList.add('is-invalid');
      input.focus();
      return;
    }
    try { localStorage.setItem('warsaken-wax-account', v); } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent('warsaken:wax-account', { detail: { actor: v } }));
    } catch (_) {}
    // renderCashbackWalletRow runs via the event listener above.
  });
  // Cancel → revert to saved wallet state.
  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-cashback-cancel]');
    if (!btn) return;
    const block = btn.closest('[data-cart-cashback]');
    if (block) renderCashbackWalletRow(block);
  });
  // Enter key inside the input fires Save.
  on(document, 'keydown', (e) => {
    if (e.key !== 'Enter') return;
    const input = e.target.closest('[data-cashback-input]');
    if (!input) return;
    e.preventDefault();
    const block = input.closest('[data-cart-cashback]');
    const saveBtn = block && block.querySelector('[data-cashback-save]');
    if (saveBtn) saveBtn.click();
  });

  /* Forms with data-cart-add */
  $$('form[data-cart-add]').forEach(form => {
    on(form, 'submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      const originalText = btn?.textContent;
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      try {
        const fd = new FormData(form);
        await addToCart(fd);
        if (btn) { btn.textContent = 'Added ✓'; setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 1800); }
      } catch (err) {
        if (btn) { btn.disabled = false; btn.textContent = originalText; }
        const msg = (err && err.description) || (err && err.message) || 'Could not add to loadout.';
        const errBox = form.querySelector('[data-form-error]');
        if (errBox) errBox.textContent = msg;
      }
    });
  });

  /* Quick-add buttons (with just a variant id). Skips list-view buttons —
     those have their own handler below that doesn't open the cart drawer. */
  $$('[data-quick-add]').forEach(btn => {
    if (btn.closest('[data-view-list]')) return;
    on(btn, 'click', async (e) => {
      e.preventDefault();
      const id = btn.dataset.quickAdd;
      if (!id) return;
      const orig = btn.textContent;
      btn.disabled = true;
      try {
        await addToCart({ id: Number(id), quantity: 1 });
        btn.textContent = 'Added';
        setTimeout(() => { btn.textContent = orig; btn.disabled = false; }, 1400);
      } catch (e) {
        btn.disabled = false;
        btn.textContent = orig;
      }
    });
  });

  /* -----------------------------------------------------------------------
     PDP variant picker + AJAX add-to-cart
     ----------------------------------------------------------------------- */
  $$('[data-product-form]').forEach(wrapper => {
    const form = wrapper.querySelector('form');
    if (!form) return;
    const dataEl = wrapper.querySelector('[data-variant-json]');
    if (!dataEl) return;
    let variants;
    try { variants = JSON.parse(dataEl.textContent); } catch { return; }

    // pdpRoot is the broader scope that contains BOTH the form wrapper and
    // the sibling elements that setVariant needs to mutate (priceEl,
    // comparePriceEl, kickerEl, gallery main image). The price row sits
    // ABOVE [data-product-form] under .pdp-info, so wrapper.querySelector
    // on those data hooks would return null and every variant click would
    // silently skip the price update.
    const pdpRoot = wrapper.closest('.pdp') || document;
    const mainImage = $('[data-pdp-main-image]', pdpRoot);

    const idInput = form.querySelector('[name="id"]');
    const priceEl = pdpRoot.querySelector('[data-product-price]');
    const comparePriceEl = pdpRoot.querySelector('[data-product-compare]');
    const submitBtn = form.querySelector('[type="submit"]');
    const optionGroups = $$('[data-option-index]', wrapper);
    const moneyFormat = (wrapper.dataset.moneyFormat || '${{amount}}');
    let eventSalePrices = {};
    try { eventSalePrices = JSON.parse(wrapper.dataset.eventSalePrices || '{}'); } catch (_) {}

    // When the variant changes (or on init), swap the main image to the
    // variant's featured_image AND repurpose the first thumb in the strip
    // to show that same image. Other thumbs stay anchored to their
    // product.media positions. Skips silently when the variant has no
    // featured_image.
    function syncMainImageForVariant(variant) {
      if (!variant || !variant.featured_image || !variant.featured_image.src) return;
      const newSrc = variant.featured_image.src;
      const sep = newSrc.includes('?') ? '&' : '?';
      const mainSrc = newSrc + sep + 'width=1400';
      const thumbSrc = newSrc + sep + 'width=80';

      // Main image swap.
      if (mainImage) {
        const mainImg = mainImage.querySelector('img');
        if (mainImg) {
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          mainImg.src = mainSrc;
        }
      }

      // First thumb mirrors the variant's image. data-full-src updates so
      // clicking the thumb later restores the variant view to the main
      // image, and the inner <img> renders the variant image so the strip
      // visually reflects the current selection.
      const thumbs = $$('[data-pdp-thumb]', pdpRoot);
      if (!thumbs.length) return;
      thumbs.forEach(t => t.classList.remove('active'));
      const firstThumb = thumbs[0];
      firstThumb.classList.add('active');
      firstThumb.dataset.fullSrc = mainSrc;
      const thumbImg = firstThumb.querySelector('img');
      if (thumbImg) {
        thumbImg.removeAttribute('srcset');
        thumbImg.removeAttribute('sizes');
        thumbImg.src = thumbSrc;
      }
    }

    // Normalise a price field to integer cents. Shopify's
    // `product.variants | json` sometimes serialises price as a number
    // ("price":2999) and sometimes as a string ("price":"29.99"); accept
    // both so the variant change handler is bullet-proof either way.
    function toCents(price) {
      if (typeof price === 'number' && isFinite(price)) return price;
      if (typeof price === 'string') {
        const num = parseFloat(price);
        if (!isFinite(num)) return null;
        // String form is dollars-with-decimal. Round to avoid floating-point
        // crumbs in the cents.
        return Math.round(num * 100);
      }
      return null;
    }
    function formatMoney(price) {
      const cents = toCents(price);
      if (cents == null) return '';
      const v = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, v);
    }

    // selectedOptions returns the active option values in Shopify's option
    // order (variant.options[]), using each group's data-option-index — NOT
    // the DOM order. This matters because the Liquid renders multi-value
    // pickers first and single-value spec rows after, so a product whose
    // options are [Color, Size (single), Style] has DOM groups in order
    // [Color, Style, Size]. Mapping by DOM order would put values at the
    // wrong indices and findVariant would never match.
    function selectedOptions() {
      const out = [];
      optionGroups.forEach(group => {
        const idx = Number(group.dataset.optionIndex);
        const select = group.querySelector('.pdp-variant-select');
        if (select) {
          out[idx] = select.value;
          return;
        }
        const active = group.querySelector('.pdp-variant.active');
        out[idx] = active ? active.dataset.optionValue : null;
      });
      return out;
    }

    function findVariant(options) {
      return variants.find(v => v.options.every((o, i) => o === options[i]));
    }

    function updateAvailability() {
      const current = selectedOptions();
      optionGroups.forEach(group => {
        const idx = Number(group.dataset.optionIndex);
        const select = group.querySelector('.pdp-variant-select');
        if (select) {
          Array.from(select.options).forEach(o => {
            const trial = current.slice();
            trial[idx] = o.value;
            const match = variants.find(v => v.options.every((opt, i) => opt === trial[i]));
            o.disabled = !match || !match.available;
          });
          return;
        }
        $$('.pdp-variant', group).forEach(opt => {
          const trial = current.slice();
          trial[idx] = opt.dataset.optionValue;
          const match = variants.find(v => v.options.every((o, i) => o === trial[i]));
          opt.disabled = !match || !match.available;
        });
      });
    }

    // Kicker also lives outside [data-product-form] (it's a sibling above
    // the form under .pdp-info), so it needs the wider pdpRoot scope too.
    const kickerEl = pdpRoot.querySelector('[data-product-kicker]');
    // DeckBook meta: "<style> : <series> : <color>". The color suffix is
    // its own span so we can swap just the color when the variant changes.
    const metaEl = pdpRoot.querySelector('[data-product-meta]');
    const colorSuffixEl = metaEl && metaEl.querySelector('[data-color-suffix]');
    const colorOptionIndex = metaEl && metaEl.dataset.colorOptionIndex != null
      ? parseInt(metaEl.dataset.colorOptionIndex, 10)
      : -1;
    function setVariant(variant) {
      if (!variant) {
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Unavailable'; }
        return;
      }
      idInput.value = variant.id;
      if (kickerEl) kickerEl.textContent = variant.id;
      if (colorSuffixEl && colorOptionIndex >= 0 && variant.options && variant.options[colorOptionIndex]) {
        colorSuffixEl.textContent = ' : ' + variant.options[colorOptionIndex];
      }
      const fiftyForFifty = !!(priceEl && priceEl.closest('[data-fifty-for-fifty]'));
      const variantCents = toCents(variant.price);
      const compareCents = toCents(variant.compare_at_price);
      const regularDisplayCents = fiftyForFifty ? Math.round(variantCents * 0.5) : variantCents;
      const sale = eventSalePrices.variants?.[String(variant.id)];
      const eventWins = variantCents != null && Date.parse(eventSalePrices.valid_until || '') > Date.now() &&
        Date.parse(sale?.valid_until || '') > Date.now() && Number.isFinite(sale?.price) && sale.price < regularDisplayCents && !idInput.form?.querySelector('[name="selling_plan"]')?.value;
      if (priceEl && variantCents != null) {
        const displayCents = eventWins ? sale.price : regularDisplayCents;
        priceEl.textContent = formatMoney(displayCents) + (fiftyForFifty && !eventWins ? '*' : '');
      }
      if (comparePriceEl) {
        if ((eventWins || fiftyForFifty) && variantCents != null) {
          comparePriceEl.textContent = formatMoney(variantCents);
          comparePriceEl.style.display = '';
        } else if (compareCents != null && variantCents != null && compareCents > variantCents) {
          comparePriceEl.textContent = formatMoney(compareCents);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }
      const eventDeal = pdpRoot.querySelector('[data-event-deal]');
      if (eventDeal) eventDeal.hidden = !eventWins;
      const eventLabel = pdpRoot.querySelector('[data-event-offer-label]');
      if (eventLabel) eventLabel.textContent = eventWins ? sale.title : '';
      const fiftyDeal = pdpRoot.querySelector('[data-fifty-deal]');
      if (fiftyDeal) fiftyDeal.hidden = eventWins;
      const regularDeal = pdpRoot.querySelector('[data-regular-deal]');
      if (regularDeal) regularDeal.hidden = eventWins || !(compareCents > variantCents);
      if (submitBtn) {
        submitBtn.disabled = !variant.available;
        submitBtn.textContent = variant.available ? (submitBtn.dataset.availableLabel || 'Add to loadout') : 'Sold out';
      }
      syncMainImageForVariant(variant);
      refreshSellingPlans(variant);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
      // Re-check whether the now-selected variant + selling plan combination
      // is already in the user's cart — overrides the button to a disabled
      // "Already in loadout" state if so.
      checkInCart();
    }

    /* In-cart check: if the current variant is already in the cart, lock
       the submit button to a disabled "Already in loadout" state.

       Match key depends on product type:
         - Subscription products: match by variant_id ALONE. Once a user
           has any line for this variant (monthly, yearly, one-time) we
           refuse to add another - subscriptions are single-line by
           nature. Switching the plan picker doesn't unlock the button.
         - Non-subscription products: match by variant_id (plan is always
           empty so it's a no-op extra check).

       Also restores the available state when the cart no longer contains
       a match (e.g. after the user removed the line from the cart).
       Re-runs on initial load, variant change, plan change, and any cart
       mutation event. */
    async function checkInCart() {
      if (!submitBtn) return;
      const variantId = String(idInput && idInput.value || '');
      if (!variantId) return;
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        const inCart = (cart.items || []).some((item) => String(item.variant_id) === variantId);
        if (inCart) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Already in loadout';
        } else if (submitBtn.textContent === 'Already in loadout') {
          // Item was removed from the cart. Restore the available label.
          // (Don't touch 'Sold out' / 'Adding…' / 'Added ✓' — those are
          // owned by setVariant / the submit handler.)
          submitBtn.disabled = false;
          submitBtn.textContent = submitBtn.dataset.availableLabel || 'Add to loadout';
        }
      } catch (_) {}
    }
    // Re-run the in-cart check whenever the cart changes (item added/removed
    // from drawer, cart page, or any other surface). The cart event is
    // dispatched by refreshCartDrawer after every successful cart mutation.
    window.addEventListener('warsaken:cart-changed', () => { checkInCart(); });

    /* -----------------------------------------------------------------
       Selling plans (Shopify Subscriptions). Picker renders once
       server-side from current_variant.selling_plan_allocations. On
       click, swap the displayed price + the hidden selling_plan input.
       On variant change, fetch the new variant's allocations and
       re-render the plan rows in place (keeping the user's selected
       plan if it still exists for the new variant; otherwise falling
       back to one-time).
       ----------------------------------------------------------------- */
    const planContainer = pdpRoot.querySelector('[data-selling-plans]');
    const planInput     = planContainer && planContainer.querySelector('[data-selling-plan-input]');
    const benefitsEl    = planContainer && planContainer.querySelector('[data-selling-plan-benefits]');
    const savingsEl     = planContainer && planContainer.querySelector('[data-benefit-savings]');
    const onetimeIntervalEl = planContainer && planContainer.querySelector('[data-onetime-interval]');

    // Parse "Delivery every 13 weeks" / "Every year" / "Monthly" → { count, unit }.
    function parsePlanInterval(name) {
      if (!name) return null;
      const m = String(name).match(/(\d+)?\s*(day|week|month|year)s?/i);
      if (!m) return null;
      return { count: m[1] ? parseInt(m[1], 10) : 1, unit: m[2].toLowerCase() };
    }
    // For display only: collapse 52 weeks / 12 months / 365 days to "1 year".
    function normalizeForDisplay(parsed) {
      if (!parsed) return parsed;
      const { count, unit } = parsed;
      if ((unit === 'week'  && count === 52) ||
          (unit === 'month' && count === 12) ||
          (unit === 'day'   && count === 365)) {
        return { count: 1, unit: 'year' };
      }
      return parsed;
    }
    // "year" / "13 weeks" — drops the leading 1 for natural "every year" phrasing.
    function intervalForSubscribe(name) {
      const p = normalizeForDisplay(parsePlanInterval(name));
      if (!p) return name || '';
      return p.count === 1 ? p.unit : (p.count + ' ' + p.unit + 's');
    }
    // "1 year" / "13 weeks" — always shows count for "Only X" phrasing.
    function intervalForOnly(name) {
      const p = normalizeForDisplay(parsePlanInterval(name));
      if (!p) return name || '';
      return p.count + ' ' + p.unit + (p.count > 1 ? 's' : '');
    }
    // Returns the raw unit count (NOT normalized) — used for ratio math
    // across plans. Assumes all plans share the same time unit per spec.
    function planRawUnits(name) {
      const p = parsePlanInterval(name);
      return p ? p.count : 0;
    }
    // Compute strike-through prices for all subscription cards.
    // base = shortest sub's unit count; compare = variant.price * (this / base).
    // Injects / removes a .pdp-selling-plan-was span next to each price.
    // Also writes the result back to data-plan-compare-cents so the savings
    // line reads off the same authoritative number.
    function applyComputedComparePrices(variantPriceCents) {
      if (!planContainer) return;
      const subBtns = $$('.pdp-selling-plan--sub', planContainer);
      if (subBtns.length === 0) return;
      const variantCents = Number(variantPriceCents);
      if (!Number.isFinite(variantCents) || variantCents <= 0) return;
      let baseUnits = Infinity;
      subBtns.forEach((btn) => {
        const u = planRawUnits(btn.dataset.planName);
        if (u > 0 && u < baseUnits) baseUnits = u;
      });
      if (baseUnits === Infinity) return;
      subBtns.forEach((btn) => {
        const units = planRawUnits(btn.dataset.planName);
        if (!units) return;
        const planPriceCents = Number(btn.dataset.planPriceCents);
        const compareCents = Math.round(variantCents * units / baseUnits);
        const priceWrap = btn.querySelector('.pdp-selling-plan-price');
        if (!priceWrap) return;
        let wasEl = priceWrap.querySelector('.pdp-selling-plan-was');
        if (compareCents > planPriceCents) {
          if (!wasEl) {
            wasEl = document.createElement('span');
            wasEl.className = 'pdp-selling-plan-was';
            const meta = priceWrap.querySelector('.pdp-selling-plan-meta');
            if (meta) priceWrap.insertBefore(wasEl, meta);
            else priceWrap.appendChild(wasEl);
          }
          wasEl.textContent = formatMoney(compareCents);
          btn.dataset.planCompareCents = compareCents;
        } else {
          if (wasEl) wasEl.remove();
          btn.dataset.planCompareCents = planPriceCents;
        }
      });
    }
    function savingsPct(priceCents, compareCents) {
      const p = Number(priceCents), c = Number(compareCents);
      if (!Number.isFinite(p) || !Number.isFinite(c) || c <= 0 || c <= p) return 0;
      return Math.round((c - p) / c * 100);
    }
    function updateBenefitsForPlan(btn) {
      if (!planContainer) return;
      const kind = btn.dataset.planKind;
      if (kind === 'sub') {
        if (benefitsEl) benefitsEl.hidden = false;
        if (savingsEl) {
          const pct = savingsPct(btn.dataset.planPriceCents, btn.dataset.planCompareCents);
          const interval = intervalForSubscribe(btn.dataset.planName);
          savingsEl.textContent = pct > 0
            ? ('Save ' + pct + '% every ' + interval)
            : ('Save every ' + interval);
        }
      } else {
        if (benefitsEl) benefitsEl.hidden = true;
      }
    }
    function updateOnetimeIntervalText(allocations) {
      // "Only X" footer on the one-time card uses the FIRST (shortest)
      // subscription plan's interval as the comparison anchor.
      if (!onetimeIntervalEl) return;
      const first = (allocations && allocations[0]) || null;
      const name = first && first.selling_plan ? first.selling_plan.name : '';
      onetimeIntervalEl.textContent = intervalForOnly(name);
    }
    // Replace the server-rendered "{{ plan.name }}" inside each Subscribe
    // card's footer with the parsed-down interval ("13 weeks" / "year").
    function paintInitialIntervals() {
      if (!planContainer) return;
      $$('.pdp-selling-plan--sub [data-plan-interval]', planContainer).forEach((el) => {
        const btn = el.closest('.pdp-selling-plan');
        if (!btn) return;
        el.textContent = intervalForSubscribe(btn.dataset.planName);
      });
      // One-time interval is server-rendered as empty; fill from first sub plan.
      const firstSub = planContainer.querySelector('.pdp-selling-plan--sub');
      if (firstSub && onetimeIntervalEl) {
        onetimeIntervalEl.textContent = intervalForOnly(firstSub.dataset.planName);
      }
    }
    paintInitialIntervals();

    // Compute strike-through compare-at prices on initial load using the
    // base variant price embedded in the picker container.
    if (planContainer) {
      applyComputedComparePrices(planContainer.dataset.baseVariantCents);
    }

    function bindPlanButtons() {
      if (!planContainer) return;
      const allPlans = $$('.pdp-selling-plan', planContainer);
      // Render initial state (first sub is .active server-side).
      const initiallyActive = planContainer.querySelector('.pdp-selling-plan.active');
      if (initiallyActive) updateBenefitsForPlan(initiallyActive);
      allPlans.forEach((btn) => {
        if (btn.dataset.boundPlan === '1') return;
        btn.dataset.boundPlan = '1';
        on(btn, 'click', () => {
          allPlans.forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
          if (planInput) planInput.value = btn.dataset.sellingPlanId || '';
          const cents = Number(btn.dataset.planPriceCents);
          if (priceEl && Number.isFinite(cents)) priceEl.textContent = formatMoney(cents);
          updateBenefitsForPlan(btn);
          // Switching plan changes the cart-line identity (same variant, new
          // plan = new line), so the "already in loadout" state needs to be
          // re-evaluated against the new selection.
          checkInCart();
        });
      });
    }
    bindPlanButtons();

    function refreshSellingPlans(variant) {
      if (!planContainer || !variant) return;
      const list = planContainer.querySelector('.pdp-selling-plan-options');
      if (!list) return;
      const prevPlanId = planInput ? planInput.value : '';
      const allocations = Array.isArray(variant.selling_plan_allocations) ? variant.selling_plan_allocations : [];

      const isoCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      const metaMarkup = ' <span class="pdp-selling-plan-meta">' + isoCode + '</span>';

      const parts = [];
      // Subscriptions first. The .pdp-selling-plan-was span is intentionally
      // omitted here — applyComputedComparePrices() injects it below with the
      // value computed from variant.price * (this_plan_units / base_units).
      allocations.forEach((a, idx) => {
        const plan = a.selling_plan || {};
        const popularClass = idx === 0 ? ' pdp-selling-plan--popular' : '';
        const popularBadge = idx === 0 ? '<span class="pdp-selling-plan-popular">Most popular</span>' : '';
        parts.push(
          '<button type="button" class="pdp-selling-plan pdp-selling-plan--sub' + popularClass + '"' +
            ' data-selling-plan-id="' + plan.id + '"' +
            ' data-plan-price-cents="' + a.price + '"' +
            ' data-plan-compare-cents="' + a.price + '"' +
            ' data-plan-name="' + (plan.name || '').replace(/"/g, '&quot;') + '"' +
            ' data-plan-kind="sub">' +
            popularBadge +
            '<div class="pdp-selling-plan-name">Subscribe</div>' +
            '<div class="pdp-selling-plan-price"><span data-plan-price>' + formatMoney(a.price) + '</span>' + metaMarkup + '</div>' +
            '<div class="pdp-selling-plan-footer">Delivery every ' + intervalForSubscribe(plan.name) + '</div>' +
          '</button>'
        );
      });
      // One-time LAST.
      parts.push(
        '<button type="button" class="pdp-selling-plan pdp-selling-plan--onetime"' +
          ' data-selling-plan-id=""' +
          ' data-plan-price-cents="' + variant.price + '"' +
          ' data-plan-kind="onetime">' +
          '<div class="pdp-selling-plan-name">One-time</div>' +
          '<div class="pdp-selling-plan-price"><span data-plan-price>' + formatMoney(variant.price) + '</span>' + metaMarkup + '</div>' +
          '<div class="pdp-selling-plan-footer">Single purchase</div>' +
        '</button>'
      );
      list.innerHTML = parts.join('');
      // Compute the new variant's compare-at prices off the same ratio
      // formula used on initial render.
      planContainer.dataset.baseVariantCents = variant.price;
      applyComputedComparePrices(variant.price);

      // Re-select previous plan if still available, else default to first sub
      // (or one-time if there are no subs).
      let target = list.querySelector('[data-selling-plan-id="' + prevPlanId + '"]');
      if (!target) target = list.querySelector('.pdp-selling-plan--sub') || list.querySelector('.pdp-selling-plan--onetime');
      if (target) {
        target.classList.add('active');
        if (planInput) planInput.value = target.dataset.sellingPlanId || '';
        const cents = Number(target.dataset.planPriceCents);
        if (priceEl && Number.isFinite(cents)) priceEl.textContent = formatMoney(cents);
        updateBenefitsForPlan(target);
      }
      bindPlanButtons();
    }

    optionGroups.forEach(group => {
      const select = group.querySelector('.pdp-variant-select');
      if (select) {
        on(select, 'change', () => {
          updateAvailability();
          const v = findVariant(selectedOptions());
          setVariant(v);
        });
      }
      $$('.pdp-variant', group).forEach(opt => {
        on(opt, 'click', () => {
          $$('.pdp-variant', group).forEach(x => x.classList.remove('active'));
          opt.classList.add('active');
          updateAvailability();
          const v = findVariant(selectedOptions());
          setVariant(v);
        });
      });
    });

    // Run the in-cart check once on initial page load. setVariant is only
    // called on user-driven variant changes, so without this the initial
    // server-rendered "Add to loadout" button never gets the chance to
    // flip to "Already in loadout" if the cart already contains a match.
    checkInCart();

    // qty
    const qtyInput = form.querySelector('[name="quantity"]');
    const qtyNum = wrapper.querySelector('[data-qty-num]');
    on(wrapper.querySelector('[data-qty-dec]'), 'click', () => {
      const v = Math.max(1, Number(qtyInput.value || 1) - 1);
      qtyInput.value = v;
      if (qtyNum) qtyNum.textContent = v;
    });
    on(wrapper.querySelector('[data-qty-inc]'), 'click', () => {
      const v = Number(qtyInput.value || 1) + 1;
      qtyInput.value = v;
      if (qtyNum) qtyNum.textContent = v;
    });

    // Gallery thumbs (pdpRoot and mainImage are defined at the top of this
    // forEach so syncMainImageForVariant can share them). Sets active state
    // across BOTH the desktop and mobile thumb sets so they stay in sync
    // when the viewport crosses the breakpoint.
    $$('[data-pdp-thumb]', pdpRoot).forEach(thumb => {
      on(thumb, 'click', () => {
        const idx = thumb.dataset.thumbIdx;
        $$('[data-pdp-thumb]', pdpRoot).forEach(t => {
          t.classList.toggle('active', t.dataset.thumbIdx === idx);
        });
        if (mainImage) {
          const src = thumb.dataset.fullSrc;
          const img = mainImage.querySelector('img');
          if (img && src) {
            // Shopify's image_tag emits srcset + sizes, which take precedence
            // over .src — so writing src alone left the original image up.
            // Drop both so the browser uses the newly-set src directly.
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            img.src = src;
          }
          mainImage.dataset.activeIdx = idx || '0';
        }
      });
    });

    /* -------- Zoom modal --------------------------------------------------
       Main image + "+" overflow thumb both open the modal. The modal
       carries its own thumbnail strip (every media item) and cross-fades
       between images when the user picks a different thumb. Any click
       inside the modal that isn't on a thumb closes — scrim, image, frame
       gutters, or close button all dismiss. */
    const zoomModal = $('[data-pdp-zoom-modal]', pdpRoot);
    if (zoomModal) {
      const zoomStage = $('[data-pdp-zoom-stage]', zoomModal);
      const zoomThumbs = $$('[data-pdp-zoom-thumb]', zoomModal);
      const zoomSrcByIdx = {};
      zoomThumbs.forEach(t => { zoomSrcByIdx[t.dataset.zoomIdx] = t.dataset.zoomSrc; });
      let zoomCurrentIdx = 0;

      function setZoomImage(idx) {
        const src = zoomSrcByIdx[idx];
        if (!src || !zoomStage) return;
        const newImg = document.createElement('img');
        newImg.src = src;
        newImg.alt = '';
        zoomStage.appendChild(newImg);
        // Force reflow so the opacity:0 starting state is committed before
        // we add .is-active — without this the browser collapses both
        // states and the fade never plays.
        void newImg.offsetWidth;
        const oldImgs = zoomStage.querySelectorAll('img:not(:last-child)');
        oldImgs.forEach(old => {
          old.classList.add('is-leaving');
          old.addEventListener('transitionend', () => old.remove(), { once: true });
          // Safety: if transitionend doesn't fire (interrupted), drop after 400ms.
          setTimeout(() => old.remove(), 400);
        });
        newImg.classList.add('is-active');
        zoomCurrentIdx = Number(idx);
        zoomThumbs.forEach(t => {
          t.classList.toggle('active', t.dataset.zoomIdx === String(idx));
        });
        // Glide the picked thumb into the centre of the scroll viewport
        // so the user doesn't have to chase the active state when the
        // row is wider than the visible strip. scrollbar is hidden in
        // CSS — this is the primary way to navigate beyond the fold.
        const activeThumb = zoomThumbs.find(t => t.dataset.zoomIdx === String(idx));
        if (activeThumb) {
          activeThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }

      function openZoom(startIdx) {
        const idx = Number(startIdx) || 0;
        zoomStage.innerHTML = '';
        zoomCurrentIdx = idx;
        const img = document.createElement('img');
        img.src = zoomSrcByIdx[idx] || '';
        img.alt = '';
        img.classList.add('is-active');
        zoomStage.appendChild(img);
        zoomThumbs.forEach(t => {
          t.classList.toggle('active', t.dataset.zoomIdx === String(idx));
        });
        zoomModal.hidden = false;
        zoomModal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('pdp-zoom-open');
        // Centre the starting thumb on open too, so the modal opens with
        // the active thumb visible in the middle of the strip (not at
        // whatever offset the scroll viewport happened to be at).
        const startThumb = zoomThumbs.find(t => t.dataset.zoomIdx === String(idx));
        if (startThumb) {
          // requestAnimationFrame so the layout settles after .hidden flips
          // off before we ask for a scroll position.
          requestAnimationFrame(() => {
            startThumb.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
          });
        }
      }
      function closeZoom() {
        zoomModal.hidden = true;
        zoomModal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('pdp-zoom-open');
        // Sync outside thumbs + main image to whatever the modal landed on,
        // so closing the modal leaves the page reflecting the last view.
        const matchOutside = $$(`[data-pdp-thumb][data-thumb-idx="${zoomCurrentIdx}"]`, pdpRoot)[0];
        if (matchOutside) matchOutside.click();
      }

      $$('[data-pdp-zoom-open]', pdpRoot).forEach(opener => {
        on(opener, 'click', (e) => {
          e.preventDefault();
          const startIdx = opener.dataset.activeIdx || mainImage?.dataset.activeIdx || '0';
          openZoom(startIdx);
        });
      });
      on(document, 'keydown', (e) => {
        if (e.key === 'Escape' && !zoomModal.hidden) closeZoom();
      });

      // One global modal click handler: thumb picks switch the image,
      // everything else (scrim, image, frame, close button) dismisses.
      on(zoomModal, 'click', (e) => {
        const thumb = e.target.closest('[data-pdp-zoom-thumb]');
        if (thumb) {
          const idx = Number(thumb.dataset.zoomIdx);
          if (idx !== zoomCurrentIdx) setZoomImage(idx);
          return;
        }
        closeZoom();
      });
    }

    // AJAX add to cart
    on(form, 'submit', async (e) => {
      e.preventDefault();
      const originalText = submitBtn?.textContent;
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Adding…'; }
      try {
        const fd = new FormData(form);
        await addToCart(fd);
        if (submitBtn) {
          submitBtn.textContent = 'Added ✓';
          // After the success-state delay, ONLY restore the original text
          // if no one else has taken over the button. addToCart triggers
          // warsaken:cart-changed which already ran checkInCart and likely
          // set "Already in loadout" + disabled. If that happened, leave
          // it alone. Otherwise restore so the button doesn't stay stuck
          // on "Added ✓".
          setTimeout(() => {
            if (submitBtn.textContent === 'Added ✓') {
              submitBtn.textContent = originalText;
              submitBtn.disabled = false;
              // Final safety re-check in case the cart-changed handler
              // missed (e.g. add succeeded but drawer refresh failed).
              checkInCart();
            }
          }, 1800);
        }
      } catch (err) {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }
        const msg = (err && err.description) || (err && err.message) || 'Could not add to loadout.';
        const errBox = wrapper.querySelector('[data-form-error]');
        if (errBox) errBox.textContent = msg;
      }
    });

    // "Add a set" — adds a playset (4) of the current variant, ignoring qty input
    const playsetBtn = wrapper.querySelector('[data-add-playset]');
    if (playsetBtn) {
      on(playsetBtn, 'click', async () => {
        const originalText = playsetBtn.textContent;
        playsetBtn.disabled = true;
        playsetBtn.textContent = 'Adding…';
        try {
          const fd = new FormData();
          fd.append('id', idInput.value);
          fd.append('quantity', '4');
          await addToCart(fd);
          playsetBtn.textContent = 'Added 4 ✓';
          setTimeout(() => { playsetBtn.textContent = originalText; playsetBtn.disabled = false; }, 1800);
        } catch (err) {
          playsetBtn.disabled = false;
          playsetBtn.textContent = originalText;
          const msg = (err && err.description) || (err && err.message) || 'Could not add to loadout.';
          const errBox = wrapper.querySelector('[data-form-error]');
          if (errBox) errBox.textContent = msg;
        }
      });
    }

    updateAvailability();

    // Sync the gallery to whichever variant the form is initialised with —
    // catches the case where Liquid rendered the product's primary image
    // even though the active variant has its own featured_image (e.g. user
    // landed on /products/X?variant=Y where Y's image differs).
    const initVariantId = parseInt(idInput.value, 10);
    const initVariant = variants.find(v => v.id === initVariantId);
    if (initVariant) syncMainImageForVariant(initVariant);
  });

  /* -----------------------------------------------------------------------
     Collection sort/filter (no-JS fallback: standard <select> submits)
     ----------------------------------------------------------------------- */
  $$('[data-sort-form]').forEach(form => {
    const sel = form.querySelector('select');
    on(sel, 'change', () => form.submit());
  });

  /* -----------------------------------------------------------------------
     Collection view toggle — grid / list
     ----------------------------------------------------------------------- */
  const viewContainer = $('[data-view-toggle]');
  const collectionViews = $('.collection-views');
  if (collectionViews) {
    const supportsGrid = collectionViews.dataset.gridOn === 'true';
    const supportsList = collectionViews.dataset.listOn === 'true';

    const applyView = (view) => {
      if (view === 'list' && !supportsList) view = 'grid';
      if (view === 'grid' && !supportsGrid) view = 'list';
      collectionViews.setAttribute('data-view', view);
      $$('[data-set-view]').forEach(btn => {
        btn.setAttribute('aria-pressed', btn.dataset.setView === view ? 'true' : 'false');
      });
    };

    // Hash takes precedence on load
    const hash = window.location.hash.replace('#', '');
    if (hash === 'list' || hash === 'grid') {
      applyView(hash);
    } else {
      applyView(collectionViews.dataset.view || 'grid');
    }

    $$('[data-set-view]').forEach(btn => {
      on(btn, 'click', () => {
        const v = btn.dataset.setView;
        applyView(v);
        history.replaceState(null, '', `#${v}`);
      });
    });
  }

  /* -----------------------------------------------------------------------
     Collection list view — optimistic UI:
     Clicking Add (or +/-) updates the row's HTML immediately, then fires
     the cart request in the background. Cart count in the header updates
     once the response lands. No section refetch — feels instant.
     ----------------------------------------------------------------------- */
  function listRowQtyHtml(variantId, qty) {
    return ''
      + '<div class="list-row-qty">'
      + '<button type="button" class="list-qty-btn" data-list-qty-change="-1" data-variant-id="' + variantId + '" data-qty="' + qty + '" aria-label="Decrease quantity">&minus;</button>'
      + '<span class="num list-qty-num">' + qty + '</span>'
      + '<button type="button" class="list-qty-btn" data-list-qty-change="1" data-variant-id="' + variantId + '" data-qty="' + qty + '" aria-label="Increase quantity">+</button>'
      + '</div>';
  }
  function listRowAddHtml(variantId) {
    return '<button type="button" class="btn btn-sm list-row-add" data-list-add="' + variantId + '">Add</button>';
  }

  async function postCartUpdate(variantId, quantity) {
    const form = new FormData();
    form.append('updates[' + variantId + ']', String(quantity));
    const res = await fetch(routes.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: form
    });
    if (!res.ok) throw await res.json().catch(() => ({}));
    return res.json();
  }

  async function syncCartCount() {
    try {
      const cart = await fetchCart();
      setCartCount(cart.item_count);
    } catch (e) { /* ignore */ }
  }

  // Delegated listener on EVERY list root. Was using $() (querySelector)
  // which only grabs the first match — on singles collections we render two
  // [data-view-list] containers (the filter-results list + the standard
  // Shopify list) and the second one's clicks fell through with no handler.
  // forEach now binds to all of them; the handler itself survives innerHTML
  // swaps on individual rows so it never needs rebinding.
  async function handleListClick(e) {
    const addBtn = e.target.closest('[data-list-add]');
    const qtyBtn = e.target.closest('[data-list-qty-change]');
    if (!addBtn && !qtyBtn) return;
    e.preventDefault();
    e.stopPropagation();

    const action = (addBtn || qtyBtn).closest('[data-list-action]');
    if (!action) return;
    const variantId = action.dataset.variantId
      || (addBtn ? addBtn.dataset.listAdd : qtyBtn.dataset.variantId);

    if (addBtn) {
      // Instant swap — UI updates BEFORE the network round-trip
      action.innerHTML = listRowQtyHtml(variantId, 1);
      try {
        await addToCart({ id: Number(variantId), quantity: 1 }, { openDrawer: false, refreshDrawer: false });
        syncCartCount();      // fast header count update
        refreshCartDrawer();  // background, don't await — syncs list view too
      } catch (err) {
        // Re-sync from server so the row reflects the actual cart, not the
        // optimistic value we just rolled back.
        syncListView();
      }
      return;
    }

    if (qtyBtn) {
      const delta = parseInt(qtyBtn.dataset.listQtyChange, 10);
      const currentQty = parseInt(qtyBtn.dataset.qty, 10) || 0;
      const newQty = Math.max(0, currentQty + delta);

      // Optimistic DOM update first
      if (newQty === 0) {
        action.innerHTML = listRowAddHtml(variantId);
      } else {
        const numEl = action.querySelector('.list-qty-num');
        if (numEl) numEl.textContent = newQty;
        action.querySelectorAll('[data-qty]').forEach(b => { b.dataset.qty = newQty; });
      }

      try {
        await postCartUpdate(variantId, newQty);
        syncCartCount();
        // Refresh the cart drawer in the background — also re-syncs every
        // [data-list-action] from the resulting cart state.
        refreshCartDrawer();
      } catch (err) {
        // Server rejected the update (most commonly: the variant isn't in
        // the cart anymore because it was cleared from the drawer while the
        // list view's data-qty was still stale). Re-sync from /cart.js
        // instead of restoring the now-wrong optimistic state.
        syncListView();
      }
    }
  }
  $$('[data-view-list]').forEach((root) => {
    root.addEventListener('click', handleListClick);
  });

  /* -----------------------------------------------------------------------
     Singles search filter (client-side, in-page)
     ----------------------------------------------------------------------- */
  const singlesSearch = $('[data-singles-search]');
  if (singlesSearch) {
    const rows = $$('[data-singles-row]');
    on(singlesSearch, 'input', () => {
      const q = singlesSearch.value.toLowerCase().trim();
      rows.forEach(row => {
        const hay = (row.dataset.search || '').toLowerCase();
        row.style.display = !q || hay.includes(q) ? '' : 'none';
      });
    });
  }

  /* -----------------------------------------------------------------------
     PDP share button — Web Share API where supported, clipboard fallback
     ----------------------------------------------------------------------- */
  $$('[data-pdp-share]').forEach(btn => {
    on(btn, 'click', async () => {
      const url = btn.dataset.shareUrl || location.href;
      const title = btn.dataset.shareTitle || document.title;
      try {
        if (navigator.share) {
          await navigator.share({ title, url });
          return;
        }
      } catch (err) {
        if (err && err.name === 'AbortError') return; // user cancelled the sheet
      }
      try {
        await navigator.clipboard.writeText(url);
        btn.classList.add('is-copied');
        clearTimeout(btn._copiedT);
        btn._copiedT = setTimeout(() => btn.classList.remove('is-copied'), 1600);
      } catch (err) {
        console.warn('[pdp-share] clipboard write failed', err);
      }
    });
  });

  /* -----------------------------------------------------------------------
     Singles PDP card flipper — 3D Y-axis flip on button click
     ----------------------------------------------------------------------- */
  $$('[data-pdp-flip]').forEach(btn => {
    on(btn, 'click', () => {
      const stage = btn.closest('.pdp-singles-stage');
      const flipper = stage && stage.querySelector('[data-pdp-flipper]');
      if (!flipper) return;
      flipper.classList.toggle('is-flipped');
      flipper.classList.add('is-flipping');
      flipper.addEventListener(
        'animationend',
        () => flipper.classList.remove('is-flipping'),
        { once: true }
      );
    });
  });

  /* -----------------------------------------------------------------------
     Singles PDP card tilt — mouse hover (desktop) + device orientation (mobile)
     ----------------------------------------------------------------------- */
  $$('[data-pdp-tilt]').forEach(tilt => {
    const stage = tilt.closest('.pdp-singles-stage');
    if (!stage) return;
    const MAX_TILT = 12; // degrees in each axis

    function setTilt(x, y) {
      // x, y in [-1, 1]; positive y tips top toward viewer (rotateX negative)
      tilt.style.setProperty('--tilt-y', String(x * MAX_TILT));
      tilt.style.setProperty('--tilt-x', String(-y * MAX_TILT));
    }
    function resetTilt() {
      tilt.style.setProperty('--tilt-y', '0');
      tilt.style.setProperty('--tilt-x', '0');
    }

    // Mouse-driven tilt
    on(stage, 'mousemove', (e) => {
      const rect = stage.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      setTilt(x, y);
    });
    on(stage, 'mouseleave', resetTilt);

    // Device orientation — calibrates to the user's resting hold on first event.
    // Up/down (beta) is damped to 20% of left/right sensitivity since most
    // people naturally rotate the phone around its long axis (gamma) far more
    // than they pitch it forward/back.
    // GAMMA_RANGE = how many degrees of phone-roll = full card tilt.
    // Smaller value = more sensitive (less phone motion → more card tilt).
    let baseBeta = null, baseGamma = null, orientationActive = false;
    const GAMMA_RANGE = 8;
    const BETA_DAMPING = 0.2;
    function handleOrientation(e) {
      const beta = e.beta || 0;     // front-back, [-180, 180]
      const gamma = e.gamma || 0;   // left-right, [-90, 90]
      if (baseBeta === null) {
        baseBeta = beta;
        baseGamma = gamma;
        return;
      }
      const dx = gamma - baseGamma;
      const dy = beta - baseBeta;
      const x = Math.max(-1, Math.min(1, dx / GAMMA_RANGE));
      const y = Math.max(-1, Math.min(1, (dy / GAMMA_RANGE) * BETA_DAMPING));
      setTilt(x, y);
    }
    function startOrientation() {
      if (orientationActive) return;
      orientationActive = true;
      window.addEventListener('deviceorientation', handleOrientation, { passive: true });
    }
    function requestOrientation() {
      if (typeof DeviceOrientationEvent !== 'undefined' &&
          typeof DeviceOrientationEvent.requestPermission === 'function') {
        // iOS 13+: explicit permission needed
        DeviceOrientationEvent.requestPermission()
          .then((state) => { if (state === 'granted') startOrientation(); })
          .catch(() => {});
      } else {
        // Android / other: start listening directly
        startOrientation();
      }
    }
    // First touch on the stage triggers the permission flow (required for iOS).
    if ('ontouchstart' in window) {
      on(stage, 'touchstart', requestOrientation, { once: true });
    }
  });

  /* -----------------------------------------------------------------------
     Scroller arrow controls — adds desktop-only left/right chevron buttons
     to any [data-scroller] horizontal scroller (section-cards + related-cards).
     The left arrow hides until the user has scrolled; the right hides when
     the scroller is at its end.
     ----------------------------------------------------------------------- */
  function initScrollerArrows(scroller) {
    if (!scroller || scroller.dataset.arrowsInit === '1') return;
    scroller.dataset.arrowsInit = '1';
    const host = scroller.closest('.section-cards-section, .related-cards') || scroller.parentElement;
    if (!host) return;

    const chevron = (dir) => `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="${dir === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'}"/>
      </svg>`;
    const leftBtn = document.createElement('button');
    leftBtn.type = 'button';
    leftBtn.className = 'scroller-arrow scroller-arrow--left is-hidden';
    leftBtn.setAttribute('aria-label', 'Scroll left');
    leftBtn.innerHTML = chevron('left');
    host.appendChild(leftBtn);

    const rightBtn = document.createElement('button');
    rightBtn.type = 'button';
    rightBtn.className = 'scroller-arrow scroller-arrow--right';
    rightBtn.setAttribute('aria-label', 'Scroll right');
    rightBtn.innerHTML = chevron('right');
    host.appendChild(rightBtn);

    const scrollAmount = () => scroller.clientWidth * 0.8;
    on(leftBtn, 'click', () => scroller.scrollBy({ left: -scrollAmount(), behavior: 'smooth' }));
    on(rightBtn, 'click', () => scroller.scrollBy({ left:  scrollAmount(), behavior: 'smooth' }));

    function updateVisibility() {
      const atStart = scroller.scrollLeft <= 1;
      const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
      leftBtn.classList.toggle('is-hidden', atStart);
      rightBtn.classList.toggle('is-hidden', atEnd);
    }
    updateVisibility();
    on(scroller, 'scroll', updateVisibility, { passive: true });
    on(window, 'resize', updateVisibility);
  }

  // Init for any scrollers already in the DOM on page load.
  $$('[data-scroller]').forEach(initScrollerArrows);

  /* -----------------------------------------------------------------------
     Mobile mega menu — animated expand / collapse.

     Native <details> can't transition its open/close state (the browser
     applies display: none to closed content), so we hijack the summary
     click, preempt the native toggle, and animate the panel's max-height
     between 0 and its measured scrollHeight. The <details> element's
     open attribute is still toggled (so a11y semantics + the [open]
     CSS rule that rotates the chevron continue to work), just at the
     right moment in the animation sequence:

       Opening:  open = true  -> measure scrollHeight -> max-height 0
                 -> RAF -> max-height target -> transitionend -> clear
                 inline max-height so future content changes flow.

       Closing:  max-height = current scrollHeight -> RAF -> max-height 0
                 -> transitionend -> open = false + clear inline.

     animating flag prevents stacking handlers when the user clicks
     rapidly during an in-flight transition.
     ----------------------------------------------------------------------- */
  const megaItems = $$('.mobile-mega-item');

  // Animate a single panel closed. Pulled out as a helper so the
  // accordion behavior below can close peer panels with the same
  // motion as a user-initiated close.
  function collapseMegaPanel(details) {
    if (!details || !details.open) return;
    const panel = details.querySelector('.mobile-mega-panel');
    if (!panel) return;
    const start = panel.scrollHeight;
    panel.style.maxHeight = start + 'px';
    requestAnimationFrame(() => {
      panel.style.maxHeight = '0px';
    });
    const onEnd = (ev) => {
      if (ev.propertyName !== 'max-height') return;
      panel.removeEventListener('transitionend', onEnd);
      details.open = false;
      panel.style.maxHeight = '';
    };
    panel.addEventListener('transitionend', onEnd);
  }

  megaItems.forEach((details) => {
    const summary = details.querySelector('summary');
    const panel = details.querySelector('.mobile-mega-panel');
    if (!summary || !panel) return;

    let animating = false;
    summary.addEventListener('click', (e) => {
      e.preventDefault();
      if (animating) return;
      animating = true;

      if (details.open) {
        // CLOSING — lock to current natural height first so the transition
        // has a from-value to interpolate from.
        const start = panel.scrollHeight;
        panel.style.maxHeight = start + 'px';
        requestAnimationFrame(() => {
          panel.style.maxHeight = '0px';
        });
        const onEnd = (ev) => {
          if (ev.propertyName !== 'max-height') return;
          panel.removeEventListener('transitionend', onEnd);
          details.open = false;
          panel.style.maxHeight = '';
          animating = false;
        };
        panel.addEventListener('transitionend', onEnd);
      } else {
        // OPENING — accordion behavior first: collapse any sibling that
        // is already open so only one panel is expanded at a time. The
        // helper animates each peer closed in parallel with this one
        // opening, so the user sees a coordinated swap rather than a
        // jump-then-grow.
        megaItems.forEach((other) => {
          if (other !== details && other.open) collapseMegaPanel(other);
        });
        // Now open this one — set open so content is laid out and
        // scrollHeight is readable, pin max-height to 0 for the
        // from-frame, then RAF to the measured target. Cleared on
        // transitionend so natural content-flow takes over and tiles
        // can wrap / reflow without an inline max-height capping them.
        details.open = true;
        const target = panel.scrollHeight;
        panel.style.maxHeight = '0px';
        requestAnimationFrame(() => {
          panel.style.maxHeight = target + 'px';
        });
        const onEnd = (ev) => {
          if (ev.propertyName !== 'max-height') return;
          panel.removeEventListener('transitionend', onEnd);
          panel.style.maxHeight = '';
          animating = false;
        };
        panel.addEventListener('transitionend', onEnd);
      }
    });
  });

  /* -----------------------------------------------------------------------
     Editorial background video: freeze-then-restart cycle.

     Pairs with the "Freeze video on last frame" + "Hold duration" settings
     on sections/editorial.liquid. Videos rendered with data-freeze-hold
     have their loop attribute already dropped server-side; this handler
     listens for the ended event, waits the configured hold duration on
     the final frame, then resets currentTime + plays again. The result
     is a deliberate pause on the final frame before each replay, which
     loop alone can't do.

     For videos with data-freeze-hold absent or 0, this handler does
     nothing - the freeze-forever case is the default browser behavior of
     a loop-less <video> at end-of-stream.
     ----------------------------------------------------------------------- */
  $$('video.editorial-video[data-freeze-hold]').forEach((video) => {
    const holdSeconds = parseFloat(video.dataset.freezeHold || '0');
    if (!Number.isFinite(holdSeconds) || holdSeconds <= 0) return;
    const holdMs = holdSeconds * 1000;
    video.addEventListener('ended', () => {
      setTimeout(() => {
        // currentTime = 0 rewinds to frame 0; play() returns a promise
        // we ignore - autoplay policy already let us start once, so a
        // muted/restart call in the same tab is unconditional.
        try { video.currentTime = 0; } catch (_) {}
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }, holdMs);
    });
  });

  /* -----------------------------------------------------------------------
     Video feature — giant play button -> full-screen YouTube modal.

     The looping image/video background is pure markup/CSS. This handler
     wires each section's play button (data-video-play="<section id>") to
     the matching modal (data-video-modal="<section id>"). On open we
     parse the YouTube ID from the section's data-youtube-url and inject
     an autoplaying nocookie iframe; on close we strip the iframe so the
     video actually stops (just hiding the modal would keep it playing in
     the background). ESC and scrim/close-button clicks all close.
     ----------------------------------------------------------------------- */
  function youTubeId(url) {
    if (!url) return '';
    // Matches watch?v= / youtu.be/ / embed/ / shorts/ forms; the ID is
    // always 11 url-safe chars.
    const m = String(url).match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : '';
  }
  function initVideoPlayButtons(scope = document) {
  $$('[data-video-play]', scope).forEach((btn) => {
    if (btn.dataset.videoModalReady === 'true') return;
    btn.dataset.videoModalReady = 'true';
    const uid = btn.dataset.videoPlay;
    const modal = document.querySelector('[data-video-modal="' + uid + '"]');
    if (!modal) return;
    const embed = modal.querySelector('[data-video-embed]');
    const closers = $$('[data-video-close="' + uid + '"]', modal);

    function openModal() {
      const id = youTubeId(modal.dataset.youtubeUrl);
      if (!id || !embed) return;
      embed.innerHTML =
        '<iframe src="https://www.youtube-nocookie.com/embed/' + id +
        '?autoplay=1&rel=0&playsinline=1" title="Video" frameborder="0" ' +
        'allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe>';
      modal.hidden = false;
      modal.dispatchEvent(new CustomEvent('video-modal:state', { bubbles: true, detail: { open: true } }));
      // Force a frame so the opacity/scale transition runs from the
      // hidden state instead of snapping straight to open.
      requestAnimationFrame(() => modal.classList.add('open'));
      document.body.style.overflow = 'hidden';
    }
    function closeModal() {
      if (modal.hidden) return;
      modal.classList.remove('open');
      document.body.style.overflow = '';
      // After the close transition, hide + tear down the iframe (stops
      // playback). Guarded so a re-open mid-transition doesn't wipe it.
      setTimeout(() => {
        if (!modal.classList.contains('open')) {
          modal.hidden = true;
          if (embed) embed.innerHTML = '';
          modal.dispatchEvent(new CustomEvent('video-modal:state', { bubbles: true, detail: { open: false } }));
        }
      }, 320);
    }

    on(btn, 'click', openModal);
    closers.forEach((c) => on(c, 'click', closeModal));
    on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('open')) closeModal();
    });
  });
  }
  initVideoPlayButtons();
  document.addEventListener('shopify:section:load', (event) => initVideoPlayButtons(event.target));

  /* Event deals: refresh the public Shopify projection, never the n8n
     calendar. Expiry checks also run when refresh fails or a tab returns. */
  (function initEventDeals() {
    if (!document.querySelector('[data-event-deals-nav], [data-event-deals-page]')) return;
    const rootUrl = new URL(routes.root_url || '/', window.location.origin);
    let feed = null;
    try {
      const initial = document.querySelector('[data-event-deals-initial]');
      if (initial) feed = JSON.parse(initial.content.textContent) || { version: 1, events: [] };
    } catch {}
    let signature = null;
    let busy = false;
    const productLoads = new WeakMap();
    const productQueue = [];
    let activeProductRequests = 0;
    function productSectionUrl(raw) {
      try {
        const source = new URL(raw, rootUrl);
        const match = source.pathname.match(/\/(products|collections)\/([a-z0-9][a-z0-9-]*)\/?$/i);
        if (source.origin !== rootUrl.origin || !match) return null;
        const url = new URL(`${match[1]}/${match[2]}`, rootUrl);
        for (const key of ['page', 'variant']) {
          const value = source.searchParams.get(key);
          if (value && /^\d+$/.test(value)) url.searchParams.set(key, value);
        }
        url.searchParams.set('section_id', 'event-deals-products');
        return url.href;
      } catch { return null; }
    }
    function runProductQueue() {
      while (activeProductRequests < 4 && productQueue.length) {
        activeProductRequests++;
        const job = productQueue.shift();
        job().finally(() => { activeProductRequests--; runProductQueue(); });
      }
    }
    function readProductSection(url) {
      return new Promise((resolve, reject) => {
        productQueue.push(async () => {
          const controller = new AbortController();
          const timeout = window.setTimeout(() => controller.abort(), 15000);
          try {
            const response = await fetch(url, { cache: 'no-store', signal: controller.signal, headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error('Eligible products unavailable');
            const results = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-event-deals-product-results]');
            if (!results) throw new Error('Product section missing');
            resolve(results);
          } catch (error) { reject(error); }
          finally { window.clearTimeout(timeout); }
        });
        runProductQueue();
      });
    }
    function hydrateProducts(page) {
      if (!page) return;
      $$('[data-event-deals-product-grid]', page).forEach(grid => {
        if (productLoads.has(grid)) return;
        const effect = grid.closest('[data-event-deals-effect]');
        if (effect?.dataset.eventDealsActive !== 'true') return;
        const actions = grid.closest('[data-event-deals-actions]');
        const loading = grid.querySelector('[data-event-deals-products-loading]');
        const error = actions.querySelector('[data-event-deals-products-error]');
        const more = actions.querySelector('[data-event-deals-products-more]');
        const retry = actions.querySelector('[data-event-deals-products-retry]');
        const seenProducts = new Set(), requested = new Set();
        let failed = [], next = [], loadingBatch = false;
        async function load(urls) {
          if (loadingBatch || effect.dataset.eventDealsActive !== 'true') return;
          loadingBatch = true;
          if (more) more.disabled = true;
          if (retry) retry.disabled = true;
          const responses = await Promise.allSettled(urls.map(readProductSection));
          if (grid.isConnected === false) return;
          failed = failed.filter(url => !urls.includes(url));
          next = next.filter(url => !urls.includes(url));
          responses.forEach((response, index) => {
            if (response.status !== 'fulfilled') { failed.push(urls[index]); return; }
            requested.add(urls[index]);
            $$('[data-event-deals-product-id]', response.value).forEach(card => {
              const id = card.dataset.eventDealsProductId;
              if (!id || seenProducts.has(id)) return;
              seenProducts.add(id); grid.append(card);
            });
            $$('[data-event-deals-products-next]', response.value).forEach(link => {
              const url = productSectionUrl(link.getAttribute('href'));
              if (url && !requested.has(url)) next.push(url);
            });
          });
          if (loading) loading.hidden = true;
          const cards = $$('[data-event-deals-product-id]', grid);
          grid.style.setProperty('--cols', cards.length && cards.every(card => card.dataset.eventDealsProductAspect === '63/88') ? '5' : '3');
          if (error) error.hidden = failed.length === 0 && (cards.length > 0 || next.length > 0);
          if (more) { more.hidden = next.length === 0; more.disabled = false; }
          if (retry) retry.disabled = false;
          loadingBatch = false;
          expire();
        }
        let sources = [];
        try { sources = JSON.parse(grid.dataset.eventDealsProductSources); } catch {}
        const initial = [...new Set((Array.isArray(sources) ? sources : []).map(link => productSectionUrl(link.url)).filter(Boolean))];
        productLoads.set(grid, load);
        more?.addEventListener('click', () => load([...new Set(next)]));
        retry?.addEventListener('click', () => load(failed.length ? [...new Set([...failed, ...next])] : initial));
        load(initial);
      });
    }
    function liveEvents(now) {
      if (feed?.version !== 1 || !Array.isArray(feed.events) || !(Date.parse(feed.valid_until) > now)) return [];
      return feed.events.filter(event => Date.parse(event.starts_at) <= now && Date.parse(event.ends_at) > now && event.effects?.length > 0);
    }
    function formatDates() {
      $$('[data-event-deals-date]').forEach(time => {
        try {
          time.textContent = new Intl.DateTimeFormat(undefined, { timeZone: time.dataset.timezone || 'UTC',
            dateStyle: 'long', timeStyle: 'short' }).format(new Date(time.dateTime)) + ` (${time.dataset.timezone || 'UTC'})`;
        } catch {}
      });
    }
    function expire() {
      // Before the first successful refresh, preserve Liquid's current state.
      if (!feed) return;
      const now = Date.now(), live = liveEvents(now), liveKeys = new Set(live.map(event => event.key));
      // Only live opted-in events expose this destination in either menu.
      $$('[data-event-deals-nav]').forEach(link => { link.hidden = live.length === 0; });
      const page = document.querySelector('[data-event-deals-page]');
      if (!page) return;
      const content = page.querySelector('[data-event-deals-content]'), empty = page.querySelector('[data-event-deals-empty]');
      if (content) content.hidden = live.length === 0;
      if (empty) empty.hidden = live.length > 0;
      const count = page.querySelector('[data-event-deals-count]');
      if (count) {
        const total = live.reduce((sum, event) => sum + event.effects.filter(effect => effect.kind !== 'shop_presentation').length, 0);
        count.textContent = `${total} ${total === 1 ? 'benefit' : 'benefits'}`;
      }
      $$('[data-event-deals-event]', page).forEach(event => { event.hidden = !liveKeys.has(event.dataset.eventDealsEvent); });
      $$('[data-event-deals-effect]', page).forEach(card => {
        if (card.dataset.eventDealsActive !== 'true' || Date.parse(card.dataset.eventDealsEffectEnd) > now) return;
        card.dataset.eventDealsActive = 'false';
        card.classList.remove('event-deals__card--active');
        card.classList.remove('event-deals__effect--active');
        const status = card.querySelector('[data-event-deals-status]');
        if (status) status.textContent = 'Ended';
        const actions = card.querySelector('[data-event-deals-actions]');
        if (actions) actions.hidden = true;
      });
    }
    async function refresh() {
      if (busy || document.hidden) return;
      busy = true;
      try {
        const stateUrl = new URL(rootUrl); stateUrl.searchParams.set('section_id', 'event-deals-state');
        const response = await fetch(stateUrl, { cache: 'no-store', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Event deals state unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const element = parsed.querySelector('[data-event-deals-state]');
        if (!element) throw new Error('Event deals state section missing');
        const incoming = JSON.parse(element.textContent);
        feed = incoming && typeof incoming === 'object' ? incoming : { version: 1, events: [] };
        const next = JSON.stringify(liveEvents(Date.now()));
        if (document.querySelector('[data-event-deals-page]') && next !== signature) {
          const pageUrl = new URL('pages/event-deals', rootUrl); pageUrl.searchParams.set('section_id', 'event-deals');
          const pageResponse = await fetch(pageUrl, { cache: 'no-store', headers: { Accept: 'text/html' } });
          if (!pageResponse.ok) throw new Error('Event deals page unavailable');
          const result = new DOMParser().parseFromString(await pageResponse.text(), 'text/html').querySelector('[data-event-deals-page]');
          if (!result) throw new Error('Event deals page section missing');
          const current = document.querySelector('[data-event-deals-page]');
          // Publish new availability immediately, then restore the user's
          // focused link when that action still exists in the new content.
          const focused = document.activeElement;
          const restoreFocus = current.contains(focused);
          const effectKey = restoreFocus ? focused.closest('[data-event-deals-effect]')?.dataset.eventDealsEffect : null;
          const href = restoreFocus ? focused.getAttribute('href') : null;
          current.replaceWith(result); signature = next;
          if (restoreFocus) {
            const card = $$('[data-event-deals-effect]', result).find(card => card.dataset.eventDealsEffect === effectKey);
            const target = card && $$('a[href]', card).find(link => link.getAttribute('href') === href);
            if (target) target.focus({ preventScroll: true });
            else { result.setAttribute('tabindex', '-1'); result.focus({ preventScroll: true }); }
          }
        } else if (!document.querySelector('[data-event-deals-page]')) signature = next;
        formatDates();
        hydrateProducts(document.querySelector('[data-event-deals-page]'));
      } catch {
        // The last successful feed retains its deadline; it cannot live forever.
      } finally { busy = false; expire(); }
    }
    formatDates();
    expire();
    hydrateProducts(document.querySelector('[data-event-deals-page]'));
    refresh();
    window.setInterval(refresh, 30000);
    window.setInterval(expire, 1000);
    document.addEventListener('visibilitychange', () => { expire(); if (!document.hidden) refresh(); });
    document.addEventListener('shopify:section:load', () => { signature = null; refresh(); });
  })();

  /* Homepage hero: eight seconds per slide. Hover/focus on controls and an
     open video modal pause the clock; manual navigation starts a fresh turn. */
  function initHomeHeroCarousel(root) {
    if (root.dataset.heroReady === 'true') return;
    root.dataset.heroReady = 'true';
    const slides = $$('[data-hero-slide]', root);
    const track = root.querySelector('.home-hero-carousel__slides');
    const dots = $$('[data-hero-dot]', root);
    if (!slides.length) return;
    const duration = 8000;
    const listeners = new AbortController();
    const options = { signal: listeners.signal };
    const hovered = new Set();
    const openModals = new Set();
    const modalIds = new Set($$('[data-video-play]', root).map(button => button.dataset.videoPlay));
    let frame = null;
    let elapsed = 0;
    let lastTick = performance.now();
    let active = 0;
    function paused() {
      const focused = document.activeElement;
      const keyboardFocus = root.contains(focused) && focused.matches(':focus-visible');
      return document.hidden || hovered.size > 0 || keyboardFocus || openModals.size > 0;
    }
    function updateProgress() {
      root.style.setProperty('--hero-progress', `${Math.min(100, elapsed / duration * 100)}%`);
      const live = slides.length > 1 && !paused() ? 'off' : 'polite';
      if (track && track.getAttribute('aria-live') !== live) track.setAttribute('aria-live', live);
    }
    function syncVideos() {
      slides.forEach((slide, index) => {
        $$('video', slide).forEach((video) => {
          if (index === active && !document.hidden && openModals.size === 0) {
            const playing = video.play();
            if (playing && typeof playing.catch === 'function') playing.catch(() => {});
          } else video.pause();
        });
      });
    }
    function show(next) {
      const previousSlide = slides[active];
      const focusWasInSlide = previousSlide && previousSlide.contains(document.activeElement);
      active = (next + slides.length) % slides.length;
      elapsed = 0;
      lastTick = performance.now();
      // A hovered control on a slide that just left the viewport must not
      // keep the new slide paused if the browser emits no pointerleave.
      hovered.forEach(control => {
        if (previousSlide !== slides[active] && previousSlide.contains(control)) hovered.delete(control);
      });
      if (track) track.style.transform = `translateX(-${active * 100}%)`;
      slides.forEach((slide, index) => {
        const visible = index === active;
        if (visible) {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('inert', '');
        }
      });
      dots.forEach((dot, index) => {
        if (index === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (focusWasInSlide && previousSlide !== slides[active]) slides[active].focus({ preventScroll: true });
      syncVideos();
      updateProgress();
    }
    function destroy() {
      if (frame !== null) cancelAnimationFrame(frame);
      listeners.abort();
      delete root.dataset.heroReady;
    }
    function tick(now) {
      if (!root.isConnected) { destroy(); return; }
      const delta = now - lastTick;
      lastTick = now;
      if (!paused()) {
        elapsed += delta;
        if (elapsed >= duration) show(active + 1);
      }
      updateProgress();
      frame = requestAnimationFrame(tick);
    }
    $$('button, a[href]', root).forEach(control => {
      control.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'touch') hovered.add(control);
      }, options);
      control.addEventListener('pointerleave', () => hovered.delete(control), options);
      control.addEventListener('pointercancel', () => hovered.delete(control), options);
    });
    dots.forEach((dot, index) => dot.addEventListener('click', () => show(index), options));
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target.closest('input, textarea, select')) return;
      event.preventDefault();
      show(active + (event.key === 'ArrowRight' ? 1 : -1));
    }, options);
    document.addEventListener('visibilitychange', () => {
      lastTick = performance.now();
      syncVideos();
      updateProgress();
    }, options);
    document.addEventListener('video-modal:state', event => {
      const id = event.target.dataset.videoModal;
      if (!modalIds.has(id)) return;
      if (event.detail.open) openModals.add(id);
      else openModals.delete(id);
      lastTick = performance.now();
      syncVideos();
      updateProgress();
    }, options);
    document.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-hero-slide]');
      if (slide && root.contains(slide)) show(slides.indexOf(slide));
    }, options);
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(root)) destroy();
    }, options);
    show(0);
    if (slides.length > 1) frame = requestAnimationFrame(tick);
  }
  $$('[data-home-hero-carousel]').forEach(initHomeHeroCarousel);
  document.addEventListener('shopify:section:load', (event) => {
    $$('[data-home-hero-carousel]', event.target).forEach(initHomeHeroCarousel);
  });

  /* -----------------------------------------------------------------------
     Related cards section — fetches buckets from the warsaken n8n webhook
     for a given source card id, then renders bucket scrollers of cards
     linked to /products/{card_id}.
     ----------------------------------------------------------------------- */
  const RELATED_CARDS_ENDPOINT = 'https://n8n.warsaken.io/webhook/cards?related=';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  // Build the canonical /full/ image URL for a card_id (e.g. "001-100"
  // -> "https://wrskn.io/001/full/100_s.jpg"). Both segments are padded
  // to three digits. _s.jpg is a smaller/faster variant — fine here since
  // the related-card image is rounded-corner clipped and only ~200px wide.
  function cardImageUrl(cardId) {
    const parts = String(cardId || '').split('-');
    if (parts.length !== 2) return '';
    const setId = parts[0].padStart(3, '0');
    const cardNo = parts[1].padStart(3, '0');
    return `https://wrskn.io/${setId}/full/${cardNo}_s.jpg`;
  }

  // Flatten buckets into a single deduped, < 300 cardno list.
  function flattenRelated(buckets) {
    const seen = new Set();
    const out = [];
    (buckets || []).forEach((bucket) => {
      (bucket.cards || []).forEach((card) => {
        const id = card && card.card_id;
        if (!id || seen.has(id)) return;
        const parts = id.split('-');
        if (parts.length !== 2) return;
        const cardNo = parseInt(parts[1], 10);
        if (!Number.isFinite(cardNo) || cardNo >= 300) return;
        seen.add(id);
        out.push(card);
      });
    });
    return out;
  }

  function renderRelatedFlat(mount, cards, foilUrl) {
    if (!cards.length) {
      mount.innerHTML = '';
      return;
    }
    const cardsHtml = cards.map((card) => {
      const imgUrl = cardImageUrl(card.card_id);
      return `
        <a class="related-card" href="/products/${esc(card.card_id)}" aria-label="${esc(card.name)}">
          <div class="related-card-image">
            <img src="${esc(imgUrl)}" alt="${esc(card.name)}" loading="lazy">
            <div class="related-card-overlay-wrap" style="--lumi-mask: url('${esc(imgUrl)}');">
              <img class="related-card-overlay" src="${esc(foilUrl)}" alt="" aria-hidden="true" loading="lazy">
            </div>
          </div>
          <div class="related-card-name">${esc(card.name)}</div>
          <div class="related-card-id mono-sm">${esc(card.card_id)}</div>
        </a>
      `;
    }).join('');
    mount.innerHTML = `
      <div class="related-cards-scroller" data-scroller>
        <div class="related-cards-track">${cardsHtml}</div>
      </div>
    `;
    const newScroller = mount.querySelector('[data-scroller]');
    if (newScroller) initScrollerArrows(newScroller);
  }

  /* -----------------------------------------------------------------------
     Card details lightbox — port of the flip-up-from-thumbnail animation
     used on news.warsaken.com. The card y-axis flips while the bounding
     rect animates from the thumbnail's location to fullscreen. Mouse-move
     adds subtle perspective tilt. Click anywhere (except the floating
     "View details" button) closes.
     ----------------------------------------------------------------------- */
  function openCardDetails(thumbnailImg, productUrl, hiResUrl, foilUrl) {
    if (!thumbnailImg) return;
    const src = hiResUrl || thumbnailImg.src;
    const bounds = thumbnailImg.getBoundingClientRect();
    let isClosing = false;
    const openAnimationTime = 1000;
    const closeAnimationTime = 400;

    // Hide the entire thumbnail image container — the bare <img> wouldn't
    // also hide its sibling foil-overlay-wrap inside .related-card-image.
    const thumbnailContainer = thumbnailImg.closest('.related-card-image') || thumbnailImg;
    thumbnailContainer.style.visibility = 'hidden';

    const animDiv = document.createElement('div');
    animDiv.className = 'card-details-anim';

    const perspective = document.createElement('div');
    perspective.className = 'card-details-perspective';
    animDiv.appendChild(perspective);

    const styleEl = document.createElement('style');
    styleEl.textContent = `
      @keyframes cardfront-open {
        0% {
          left: ${bounds.left}px;
          top: ${bounds.top}px;
          right: ${window.innerWidth - (bounds.left + bounds.width)}px;
          bottom: ${window.innerHeight - (bounds.top + bounds.height)}px;
          transform: rotate3d(0, 0, 0, 0deg) translate(0px, 0px) scale(0.87) translateZ(0px);
          opacity: 1;
        }
        5% { transform: rotate3d(0, 1, 0, -77deg) translate(0px, 0px) scale(0.87) translateZ(0px); opacity: 1; }
        5.00000001% { transform: rotate3d(0, 1, 0, 100deg) translate(0px, 0px) scale(0.87) translateZ(0px); opacity: 0; }
        20% { transform: rotate3d(0, 1, 0, -87deg) translate(100px, 0px) scale(0.87) translateZ(0px); opacity: 0; }
        20.00000001% { transform: rotate3d(0, 1, 0, 90deg) translate(100px, 0px) scale(0.87) translateZ(0px); opacity: 1; }
        40% { left: 0px; top: 0px; bottom: 0px; right: 0px; transform: rotate3d(0, 1, 0, 10deg) translate(0px, 0px) scale(0.87) translateZ(0px); opacity: 1; }
        100% { left: 0px; top: 0px; bottom: 0px; right: 0px; transform: rotate3d(0, 1, 0, 0deg) translate(0px, 0px) scale(0.87) translateZ(20px); opacity: 1; }
      }
      @keyframes cardback-open {
        0% {
          left: ${bounds.left}px;
          top: ${bounds.top}px;
          right: ${window.innerWidth - (bounds.left + bounds.width)}px;
          bottom: ${window.innerHeight - (bounds.top + bounds.height)}px;
          transform: rotate3d(0, 0, 0, 0deg) translate(0px, 0px) scale(0.87) translateZ(0px);
          opacity: 0;
        }
        5% { transform: rotate3d(0, 1, 0, -70deg) translate(0px, 0px) scale(0.87) translateZ(0px); opacity: 0; }
        5.00000001% { transform: rotate3d(0, 1, 0, 110deg) translate(0px, 0px) scale(0.87) translateZ(0px); opacity: 1; }
        20% { transform: rotate3d(0, 1, 0, -87deg) translate(100px, 0px) scale(0.87) translateZ(0px); opacity: 1; }
        20.00000001% { transform: rotate3d(0, 1, 0, 90deg) translate(100px, 0px) scale(0.87) translateZ(0px); opacity: 0; }
        40% { left: 0px; top: 0px; bottom: 0px; right: 0px; transform: rotate3d(0, 1, 0, 10deg) translate(0px, 0px) scale(0.87) translateZ(0px); opacity: 0; }
        100% { left: 0px; top: 0px; bottom: 0px; right: 0px; transform: rotate3d(0, 1, 0, 0deg) translate(0px, 0px) scale(0.87) translateZ(20px); opacity: 0; }
      }
      @keyframes cardfront-close {
        100% {
          left: ${bounds.left}px;
          top: ${bounds.top}px;
          right: ${window.innerWidth - (bounds.left + bounds.width)}px;
          bottom: ${window.innerHeight - (bounds.top + bounds.height)}px;
          transform: rotate3d(0, 0, 0, 0deg) translate(0px, 0px) scale(1) translateZ(0px);
          opacity: 1;
        }
        55.00000001% { transform: rotate3d(0, 1, 0, -70deg) translate(0px, 0px) scale(1) translateZ(20px); opacity: 1; }
        55% { transform: rotate3d(0, 1, 0, 120deg) translate(0px, 0px) scale(1) translateZ(20px); opacity: 0; }
        10.00000001% { transform: rotate3d(0, 1, 0, -87deg) translate(0px, 0px) scale(0.75) translateZ(20px); opacity: 0; }
        10% { transform: rotate3d(0, 1, 0, 90deg) translate(0px, 0px) scale(0.75) translateZ(20px); opacity: 1; }
        0% { left: 0px; top: 0px; bottom: 0px; right: 0px; transform: rotate3d(0, 1, 0, 0deg) translate(0px, 0px) scale(1.0) translateZ(20px); opacity: 1; }
      }
      @keyframes cardback-close {
        100% {
          left: ${bounds.left}px;
          top: ${bounds.top}px;
          right: ${window.innerWidth - (bounds.left + bounds.width)}px;
          bottom: ${window.innerHeight - (bounds.top + bounds.height)}px;
          transform: rotate3d(0, 0, 0, 0deg) translate(0px, 0px) scale(1.295) translateZ(0px);
          opacity: 0;
        }
        55.00000001% { transform: rotate3d(0, 1, 0, -70deg) translate(0px, 0px) scale(1) translateZ(20px); opacity: 0; }
        55% { transform: rotate3d(0, 1, 0, 120deg) translate(0px, 0px) scale(1) translateZ(20px); opacity: 1; }
        10.00000001% { transform: rotate3d(0, 1, 0, -87deg) translate(0px, 0px) scale(0.75) translateZ(20px); opacity: 1; }
        10% { transform: rotate3d(0, 1, 0, 90deg) translate(0px, 0px) scale(0.75) translateZ(20px); opacity: 0; }
        0% { left: 0px; top: 0px; bottom: 0px; right: 0px; transform: rotate3d(0, 1, 0, 0deg) translate(0px, 0px) scale(1.0) translateZ(20px); opacity: 0; }
      }
    `;
    document.head.appendChild(styleEl);

    const back = document.createElement('div');
    back.className = 'card-details-back';
    back.style.backgroundImage = "url('https://wrskn.io/assets/sleeves/default/default_r.png')";
    perspective.appendChild(back);

    const card = document.createElement('div');
    card.className = 'card-details-card';
    card.style.backgroundImage = `url('${src}')`;
    perspective.appendChild(card);

    // Foil layer as a CHILD of the card div, so it inherits the card's
    // flip animation (left/top/right/bottom + rotateY) and CSS opacity
    // cascade — foil flies in and out with the card automatically and
    // disappears during the back-facing half of the flip.
    if (foilUrl) {
      const foilWrap = document.createElement('div');
      foilWrap.className = 'card-details-foil-wrap';
      foilWrap.style.setProperty('--lumi-mask', `url("${src}")`);
      const foilImg = document.createElement('img');
      foilImg.className = 'card-details-foil';
      foilImg.src = foilUrl;
      foilImg.alt = '';
      foilImg.setAttribute('aria-hidden', 'true');
      foilWrap.appendChild(foilImg);
      card.appendChild(foilWrap);
    }

    const overlay = document.createElement('div');
    overlay.className = 'card-details-overlay';
    animDiv.appendChild(overlay);

    // Floating "View details" button — fades in after the open animation lands.
    const btn = document.createElement('a');
    btn.className = 'btn card-details-view';
    btn.href = productUrl || '#';
    btn.textContent = 'View details';
    btn.style.opacity = '0';
    animDiv.appendChild(btn);
    const btnFadeTimer = setTimeout(() => {
      btn.style.opacity = '1';
    }, openAnimationTime);

    // Subtle perspective tilt on mouse move + parallax CSS vars for the foil
    // (--tilt-x / --tilt-y are inherited by .card-details-foil's translate calc).
    const handleMouseMove = (event) => {
      if (isClosing) return;
      const box = perspective.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;
      requestAnimationFrame(() => {
        const calcX = -(y - box.y - (box.height / 2)) / (box.height / 25);
        const calcY = (x - box.x - (box.width / 2)) / (box.width / 25);
        perspective.style.transform = `perspective(2000px) rotateX(${calcX}deg) rotateY(${calcY}deg)`;
        perspective.style.setProperty('--tilt-x', String(calcX));
        perspective.style.setProperty('--tilt-y', String(calcY));
        if (perspective.style.transition) {
          setTimeout(() => { perspective.style.transition = ''; }, openAnimationTime);
        }
      });
    };
    const resetPerspective = () => {
      requestAnimationFrame(() => {
        perspective.style.transition = 'transform 250ms';
        perspective.style.transform = 'perspective(2000px) rotateX(0deg) rotateY(0deg)';
        perspective.style.setProperty('--tilt-x', '0');
        perspective.style.setProperty('--tilt-y', '0');
      });
    };

    const originalOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';

    const handleClose = () => {
      if (isClosing) return;
      isClosing = true;
      clearTimeout(btnFadeTimer);
      back.className = 'card-details-back-close';
      card.className = 'card-details-card-close';
      btn.style.opacity = '0';
      resetPerspective();
      setTimeout(() => {
        animDiv.remove();
        styleEl.remove();
        window.removeEventListener('resize', resizeListener);
        document.removeEventListener('keydown', escHandler);
        document.documentElement.style.overflow = originalOverflow;
        thumbnailContainer.style.visibility = '';
      }, closeAnimationTime);
    };

    const resizeListener = () => handleClose();
    window.addEventListener('resize', resizeListener);

    const escHandler = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', escHandler);

    overlay.addEventListener('click', handleClose);
    overlay.addEventListener('mousemove', handleMouseMove);
    overlay.addEventListener('mouseout', resetPerspective);

    document.body.appendChild(animDiv);
  }

  // Delegated: any click inside a related-cards section on a card tile
  // opens the flip-up preview instead of navigating.
  on(document, 'click', (e) => {
    const card = e.target.closest('.related-card');
    if (!card) return;
    const section = card.closest('[data-related-cards]');
    if (!section) return;
    e.preventDefault();
    const img = card.querySelector('.related-card-image > img');
    if (!img) return;
    // For the fullscreen preview, swap the _s.jpg thumbnail for the .png
    // high-res variant from the same wrskn.io path.
    const hiRes = card.dataset.fullresUrl || img.src.replace(/_s\.jpg$/, '.png');
    const foilUrl = section.dataset.foilUrl || '';
    openCardDetails(img, card.getAttribute('href') || '', hiRes, foilUrl);
  });

  $$('[data-related-cards]').forEach(async (root) => {
    const mount = root.querySelector('[data-related-cards-mount]');
    if (!mount) return;
    const sourceId = (root.dataset.sourceCardId || '').trim();
    const foilUrl = root.dataset.foilUrl || '';
    if (!sourceId) {
      root.style.display = 'none';
      return;
    }
    try {
      const res = await fetch(
        RELATED_CARDS_ENDPOINT + encodeURIComponent(sourceId),
        { headers: { Accept: 'application/json' } }
      );
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      const cards = flattenRelated(data && data.buckets);
      renderRelatedFlat(mount, cards, foilUrl);
      if (!mount.children.length) root.style.display = 'none';
    } catch (err) {
      console.warn('[related-cards] fetch failed:', err);
      root.style.display = 'none';
    }
  });

  /* -----------------------------------------------------------------------
     Singles collection filter — lazy meta fetch, live filter panel, and
     API-driven result grid that replaces the Shopify-rendered grid when
     any filter is active. Only initialized on collections that opt in
     via [data-singles-filter-root].
     ----------------------------------------------------------------------- */
  $$('[data-singles-filter-root]').forEach((root) => {
    const apiBase = root.dataset.cardsApi || 'https://n8n.warsaken.io/webhook/cards';
    const foilUrl = root.dataset.foilUrl || '';
    // Set-scoped pages (collection handle is purely numeric like "950") lock
    // the Set filter to the current collection. The Set section is hidden
    // in the panel and the API call always carries set=<scoped> so the
    // result list stays inside this set regardless of which other filters
    // the user toggles. null on non-set pages (e.g. /collections/cards).
    const collectionHandle = root.dataset.collectionHandle || '';
    const scopedSet = /^\d+$/.test(collectionHandle) ? collectionHandle : null;
    // Map of zero-padded set handle ("001") -> collection title ("Genesis of
    // Conflict"). Built server-side from Shopify collections; empty if no
    // matching numeric-handle collections exist.
    let setNames = {};
    try { setNames = JSON.parse(root.dataset.setNames || '{}'); } catch (_) {}
    // Filter results come from the card catalog, which has no Shopify price
    // or collection membership. Resolve each displayed product on the shop.
    const resolvedPrices = new Map();
    const priceQueue = [];
    let priceRequests = 0;
    function resolveFilterPrices() {
      $$('[data-dynamic-product-price]', root).forEach(el => {
        const handle = el.dataset.dynamicProductPrice;
        if (!handle || el.dataset.priceQueued) return;
        el.dataset.priceQueued = 'true';
        priceQueue.push({ handle, el });
      });
      function pump() {
        while (priceRequests < 4 && priceQueue.length) {
          const { handle, el } = priceQueue.shift();
          priceRequests++;
          let task = resolvedPrices.get(handle);
          if (task && task.expiresAt <= Date.now()) { resolvedPrices.delete(handle); task = null; }
          if (!task) {
            task = fetch(`/products/${encodeURIComponent(handle)}?section_id=event-product-price`, { cache: 'no-store', headers: { Accept: 'text/html' } })
              .then(r => { if (!r.ok) throw new Error('Product price unavailable'); return r.text(); })
              .then(html => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const now = doc.querySelector('[data-resolved-price-now]')?.textContent.trim();
                const was = doc.querySelector('[data-resolved-price-was]')?.textContent.trim();
                if (!now || !was) throw new Error('Product price unavailable');
                const sale = JSON.parse(doc.querySelector('[data-resolved-product-price]')?.dataset.resolvedEventPrice || '{}');
                const deadline = Date.parse(sale.valid_until || '');
                return { now, was, validUntil: deadline > Date.now() ? deadline : Date.now() + 60000 };
              });
            resolvedPrices.set(handle, task);
          }
          task.then(price => {
            task.expiresAt = price.validUntil;
            const now = el.querySelector('[data-dynamic-price-now]');
            const was = el.querySelector('[data-dynamic-price-was]');
            if (now) now.textContent = price.now;
            if (was) was.textContent = price.was;
          }).catch(() => {
            // Do not claim an unverified representative sale price.
            const now = el.querySelector('[data-dynamic-price-now]');
            const was = el.querySelector('[data-dynamic-price-was]');
            if (now) now.textContent = 'View price';
            if (was) was.textContent = '';
            resolvedPrices.delete(handle);
          }).finally(() => { priceRequests--; pump(); });
        }
      }
      pump();
    }
    const priceObserver = new MutationObserver(resolveFilterPrices);
    priceObserver.observe(root, { childList: true, subtree: true });

    // Render-time helper: show the shop's collection title for a set id
    // (falling back to the zero-padded id if no matching collection title).
    function setDisplay(v) {
      const handle = String(v).padStart(3, '0');
      return setNames[handle] || handle;
    }
    const panel = root.querySelector('[data-singles-filter-panel]');
    const scrim = root.querySelector('[data-singles-filter-scrim]');
    const toggleBtn = root.querySelector('[data-singles-filter-toggle]');
    const closeBtns = $$('[data-singles-filter-close], [data-singles-filter-close-action]', root);
    const clearBtn = root.querySelector('[data-singles-filter-clear]');
    const body = root.querySelector('[data-singles-filter-body]');
    const badge = root.querySelector('[data-filter-active-count]');
    const resultsRoot = root.querySelector('[data-singles-filter-results]');
    const resultsGrid = root.querySelector('[data-singles-filter-grid]');
    const resultsList = root.querySelector('[data-singles-filter-list]');
    const resultsStatus = root.querySelector('[data-singles-filter-status]');
    const pager = root.querySelector('[data-singles-filter-pager]');
    const loadMoreBtn = root.querySelector('[data-singles-filter-load-more]');
    const shopifyViews = root.querySelector('[data-singles-shopify-views]');
    if (!panel || !toggleBtn) return;

    let metaLoaded = false;
    let metaLoading = false;
    let currentMeta = null;
    let fetchSeq = 0;
    let debounceTimer = null;
    let lastPage = 1;
    let lastHasMore = false;
    // Shared by buildQuery (request) and runFetch (full-page inference for
    // missing pagination metadata). Keep these in lockstep.
    const PER_PAGE = 50;
    // Active selection state — keyed by canonical filter id.
    // For lists: Set<string>. For numerics: { min, max }.
    const state = {};

    // ---------- Open / close panel ----------
    function setPanelOpen(open) {
      panel.hidden = !open;
      scrim.hidden = !open;
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.classList.toggle('singles-filter-open', open);
      if (open && !metaLoaded && !metaLoading) loadMeta();
    }
    on(toggleBtn, 'click', () => setPanelOpen(panel.hidden));
    closeBtns.forEach(b => on(b, 'click', () => setPanelOpen(false)));
    on(scrim, 'click', () => setPanelOpen(false));
    on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && !panel.hidden) setPanelOpen(false);
    });

    on(clearBtn, 'click', () => {
      Object.keys(state).forEach(k => delete state[k]);
      // Reset UI controls to defaults
      body.querySelectorAll('[data-filter-chip].is-active').forEach(c => c.classList.remove('is-active'));
      body.querySelectorAll('[data-filter-check].is-active').forEach(c => {
        c.classList.remove('is-active');
        c.setAttribute('aria-checked', 'false');
      });
      // Radios: re-select the "All" option (value="")
      body.querySelectorAll('[data-filter-radio]').forEach(r => {
        const isAll = r.dataset.filterValue === '';
        r.classList.toggle('is-active', isAll);
        r.setAttribute('aria-checked', isAll ? 'true' : 'false');
      });
      body.querySelectorAll('[data-filter-range]').forEach(group => {
        const min = group.querySelector('[data-filter-range-min]');
        const max = group.querySelector('[data-filter-range-max]');
        const detailsEl = group.closest('details');
        const label = detailsEl ? detailsEl.querySelector('[data-filter-range-label]') : null;
        if (min) min.value = min.min;
        if (max) max.value = max.max;
        if (label && min && max) label.textContent = `${min.value}–${max.value}`;
        group.style.setProperty('--range-min-pct', 0);
        group.style.setProperty('--range-max-pct', 100);
      });
      updateBadge();
      hideResults();
    });

    // ---------- Meta fetch + UI build ----------
    async function loadMeta() {
      metaLoading = true;
      try {
        const res = await fetch(`${apiBase}?meta=filters`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        currentMeta = await res.json();
        metaLoaded = true;
        renderFilterUI(currentMeta);
      } catch (err) {
        console.warn('[singles-filter] meta fetch failed', err);
        body.innerHTML = '<div class="mono-sm" style="color: var(--mute);">Couldn\'t load filters — try again later.</div>';
      } finally {
        metaLoading = false;
      }
    }

    // Filter definitions — id, label, type, source field name in meta, options.
    // We hand-pick which meta filters to render and in what order, so the
    // user-facing UI doesn't blindly track schema additions.
    const FILTER_DEFS = [
      { id: 'set',         label: 'Set',          kind: 'radio-list',  source: 'set',         sort: 'alnum', allLabel: 'All sets' },
      { id: 'type',        label: 'Type',         kind: 'radio-list',  source: 'type',        sort: 'alnum', allLabel: 'All types',
        only: ['Leader','Territory','Force','WMD','Building','Intel','Morale','Weather'] },
      { id: 'subtype',     label: 'Subtype',      kind: 'radio-list',  source: 'subtype',     sort: 'alnum', allLabel: 'All subtypes' },
      { id: 'rank',        label: 'Rank',         kind: 'list-multi',  source: 'rank',        sort: 'alnum' },
      { id: 'cost_time',   label: 'Cost timer',   kind: 'range',       min: 0, max: 6,   step: 1 },
      { id: 'cost_total',  label: 'Resource cost', kind: 'range',      min: 0, max: 9,   step: 1 },
      { id: 'produces',    label: 'Produces',     kind: 'list-multi',  source: 'produces',
        only: ['Generic','Food','Power','Fuel','Equipment'],
        valueMap: { 'Generic':'generic','Food':'food','Power':'power','Fuel':'fuel','Equipment':'equipment' } },
      { id: 'health',      label: 'Health',       kind: 'range',       min: 10, max: 900, step: 10 },
      { id: 'attack',      label: 'Attack',       kind: 'range',       min: 0,  max: 900, step: 10 },
      { id: 'ability',     label: 'Abilities',    kind: 'ability-check-list', source: 'ability', sort: 'alnum' },
    ];

    // Strip parametric tags and trigger-type suffixes from ability titles.
    // The cards API delivers labels like "(X) : DRAW A CARD : TRIGGERED" or
    // "(X) (GENERIC) PER TURN"; for a filter checkbox we just want the verb.
    function cleanAbilityLabel(title) {
      return String(title || '')
        .replace(/^\([^)]*\)\s*:\s*/, '')   // leading "(X) : "
        .replace(/\([^)]*\)\s*/g, '')        // any "(SOMETHING) " inline
        .replace(/\s*:\s*[^:]+$/, '')        // trailing " : TYPE"
        .replace(/\s{2,}/g, ' ')             // collapse double-spaces left over
        .trim();
    }

    function alnumSort(a, b) {
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    }

    // Filter arr to the values in `only`, preserving the order of `only`
    // (not the order they happened to arrive in from the API). Used by Type
    // to lock the radio list to the canonical Leader / Territory / Force /
    // WMD / Building / Intel / Morale / Weather order.
    function pickAndOrderFromOnly(arr, only) {
      if (!only) return arr;
      const map = new Map();
      arr.forEach(v => {
        const key = String(typeof v === 'object' ? (v.title || v.function || v) : v).toLowerCase();
        map.set(key, v);
      });
      return only.map(o => map.get(String(o).toLowerCase())).filter(Boolean);
    }

    function renderFilterUI(meta) {
      const filters = (meta && meta.filters) || {};
      const out = [];
      FILTER_DEFS.forEach(def => {
        // On set-scoped pages the Set filter is hidden entirely — the API
        // call below always pins it to the current collection's set.
        if (scopedSet && def.id === 'set') return;
        if (def.kind === 'radio-list' || def.kind === 'list-multi') {
          const src = filters[def.source];
          if (!src || !Array.isArray(src.values)) return;
          let values = src.values.slice();
          if (def.only) {
            // Authored "only" list wins on ORDER as well as membership; skip
            // alnum sort so e.g. Type renders Leader first, Weather last.
            values = pickAndOrderFromOnly(values, def.only);
          } else if (def.sort === 'alnum') {
            values.sort(alnumSort);
          }
          if (def.kind === 'radio-list') {
            out.push(renderRadioList(def, values));
          } else {
            out.push(renderChipMulti(def, values));
          }
        } else if (def.kind === 'ability-check-list') {
          const src = filters[def.source];
          if (!src || !Array.isArray(src.values)) return;
          // ability values are { function, title } objects; sort by cleaned label
          let values = src.values.slice().sort((a, b) =>
            alnumSort(cleanAbilityLabel(a.title || a.function), cleanAbilityLabel(b.title || b.function))
          );
          out.push(renderAbilityCheckList(def, values));
        } else if (def.kind === 'range') {
          out.push(renderRange(def));
        }
      });
      body.innerHTML = out.join('');
      attachFilterHandlers();
    }

    // Each filter section is wrapped in a native <details> so it can be
    // expanded / collapsed without JS. Only "type" defaults to open per spec;
    // every other section starts collapsed.
    function wrapGroup(def, contentHtml, summaryMetaHtml) {
      const openAttr = def.id === 'type' ? ' open' : '';
      return `
        <details class="singles-filter-group"${openAttr}>
          <summary class="singles-filter-group-summary">
            <span class="singles-filter-group-label mono-sm">${esc(def.label)}</span>
            <span class="singles-filter-summary-meta">
              ${summaryMetaHtml || ''}
              <svg class="singles-filter-group-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M3 5l3 3 3-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </summary>
          <div class="singles-filter-group-content">
            ${contentHtml}
          </div>
        </details>`;
    }

    function renderRadioList(def, values) {
      const items = [
        `<li><button type="button" class="singles-filter-option is-active" data-filter-radio data-filter-id="${esc(def.id)}" data-filter-value="" role="radio" aria-checked="true"><span class="singles-filter-dot"></span><span class="singles-filter-option-label">${esc(def.allLabel || 'All')}</span></button></li>`
      ].concat(values.map(v => {
        // For Set values, look up the shop's collection title; everything
        // else displays the raw value.
        const display = def.id === 'set' ? setDisplay(v) : v;
        return `<li><button type="button" class="singles-filter-option" data-filter-radio data-filter-id="${esc(def.id)}" data-filter-value="${esc(v)}" role="radio" aria-checked="false"><span class="singles-filter-dot"></span><span class="singles-filter-option-label">${esc(display)}</span></button></li>`;
      })).join('');
      return wrapGroup(def, `<ul class="singles-filter-option-list" role="radiogroup">${items}</ul>`);
    }

    function renderChipMulti(def, values) {
      const chips = values.map(v => {
        const display = def.valueMap ? v : v;
        const sent = def.valueMap ? def.valueMap[v] : v;
        return `<button type="button" class="singles-filter-chip" data-filter-chip data-filter-id="${esc(def.id)}" data-filter-value="${esc(sent)}">${esc(display)}</button>`;
      }).join('');
      return wrapGroup(def, `<div class="singles-filter-chips">${chips}</div>`);
    }

    function renderAbilityCheckList(def, values) {
      const items = values.map(v => {
        const display = cleanAbilityLabel(v.title || v.function);
        const sent = v.function;
        return `<li><button type="button" class="singles-filter-option" data-filter-check data-filter-id="${esc(def.id)}" data-filter-value="${esc(sent)}" role="checkbox" aria-checked="false"><span class="singles-filter-dot"></span><span class="singles-filter-option-label">${esc(display)}</span></button></li>`;
      }).join('');
      return wrapGroup(def, `<ul class="singles-filter-option-list">${items}</ul>`);
    }

    function renderRange(def) {
      const step = def.step || 1;
      const summaryMeta = `<span class="singles-filter-range-label num" data-filter-range-label>${def.min}–${def.max}</span>`;
      const content = `
        <div class="singles-filter-range" data-filter-range data-filter-id="${esc(def.id)}" data-filter-min="${def.min}" data-filter-max="${def.max}" style="--range-min-pct: 0; --range-max-pct: 100;">
          <input type="range" min="${def.min}" max="${def.max}" step="${step}" value="${def.min}" data-filter-range-min aria-label="${esc(def.label)} minimum">
          <input type="range" min="${def.min}" max="${def.max}" step="${step}" value="${def.max}" data-filter-range-max aria-label="${esc(def.label)} maximum">
        </div>`;
      return wrapGroup(def, content, summaryMeta);
    }

    function attachFilterHandlers() {
      // Radio (single-select circle-checkbox list)
      body.querySelectorAll('[data-filter-radio]').forEach(btn => {
        on(btn, 'click', () => {
          const id = btn.dataset.filterId;
          const value = btn.dataset.filterValue;
          body.querySelectorAll(`[data-filter-radio][data-filter-id="${id}"]`).forEach(other => {
            const on = other === btn;
            other.classList.toggle('is-active', on);
            other.setAttribute('aria-checked', on ? 'true' : 'false');
          });
          if (value) state[id] = new Set([value]);
          else delete state[id];
          scheduleFetch();
        });
      });
      // Check (multi-select circle-checkbox list)
      body.querySelectorAll('[data-filter-check]').forEach(btn => {
        on(btn, 'click', () => {
          const id = btn.dataset.filterId;
          const value = btn.dataset.filterValue;
          btn.classList.toggle('is-active');
          const checked = btn.classList.contains('is-active');
          btn.setAttribute('aria-checked', checked ? 'true' : 'false');
          if (!state[id]) state[id] = new Set();
          if (checked) state[id].add(value);
          else state[id].delete(value);
          if (state[id].size === 0) delete state[id];
          scheduleFetch();
        });
      });
      // Chip multi-select (still used for Rank, Produces)
      body.querySelectorAll('[data-filter-chip]').forEach(chip => {
        on(chip, 'click', () => {
          const id = chip.dataset.filterId;
          const value = chip.dataset.filterValue;
          chip.classList.toggle('is-active');
          if (!state[id]) state[id] = new Set();
          if (chip.classList.contains('is-active')) state[id].add(value);
          else state[id].delete(value);
          if (state[id].size === 0) delete state[id];
          scheduleFetch();
        });
      });
      // Range sliders — also drive --range-min-pct / --range-max-pct so the
      // thick selected-range bar between thumbs can be drawn in pure CSS.
      body.querySelectorAll('[data-filter-range]').forEach(group => {
        const id = group.dataset.filterId;
        const groupMin = parseInt(group.dataset.filterMin, 10);
        const groupMax = parseInt(group.dataset.filterMax, 10);
        const minEl = group.querySelector('[data-filter-range-min]');
        const maxEl = group.querySelector('[data-filter-range-max]');
        // Range label now lives in the section's <summary>, not in
        // group.parentElement — climb to the enclosing <details> first.
        const detailsEl = group.closest('details');
        const label = detailsEl ? detailsEl.querySelector('[data-filter-range-label]') : null;
        const sync = (movedEl) => {
          let a = parseInt(minEl.value, 10);
          let b = parseInt(maxEl.value, 10);
          if (a > b) {
            if (movedEl === minEl) maxEl.value = String(a);
            else minEl.value = String(b);
            a = parseInt(minEl.value, 10);
            b = parseInt(maxEl.value, 10);
          }
          const span = (groupMax - groupMin) || 1;
          group.style.setProperty('--range-min-pct', ((a - groupMin) / span) * 100);
          group.style.setProperty('--range-max-pct', ((b - groupMin) / span) * 100);
          label.textContent = `${a}–${b}`;
          if (a === groupMin && b === groupMax) delete state[id];
          else state[id] = { min: a, max: b };
          scheduleFetch();
        };
        on(minEl, 'input', () => sync(minEl));
        on(maxEl, 'input', () => sync(maxEl));
      });
    }

    // ---------- Fetch results ----------
    function buildQuery(page) {
      const params = new URLSearchParams();
      let activeFilters = 0;
      Object.keys(state).forEach(id => {
        const v = state[id];
        if (v instanceof Set && v.size > 0) {
          params.set(id, Array.from(v).join(','));
          activeFilters++;
        } else if (v && typeof v === 'object' && 'min' in v) {
          params.set(`${id}_min`, String(v.min));
          params.set(`${id}_max`, String(v.max));
          activeFilters++;
        }
      });
      // On set-scoped pages, always pin set=<scoped> regardless of which
      // other filters the user toggled. This isn't counted as an active
      // filter — the user-facing badge only reflects filters the user
      // explicitly chose.
      if (scopedSet) {
        params.set('set', scopedSet);
      }
      // PER_PAGE per request — gives the user a real lazy-loaded grid that
      // the Load more button actually advances. Was 250 (the API cap) which
      // meant most filter sets resolved in one fetch and Load more would
      // either not show or fire a request that returned nothing new.
      params.set('per_page', String(PER_PAGE));
      params.set('page', String(page || 1));
      return { qs: params.toString(), activeFilters };
    }

    function scheduleFetch() {
      updateBadge();
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => runFetch(1), 250);
    }

    function updateBadge() {
      const count = Object.keys(state).length;
      if (badge) {
        badge.textContent = String(count);
        badge.hidden = count === 0;
      }
    }

    async function runFetch(page) {
      const { qs, activeFilters } = buildQuery(page);
      if (activeFilters === 0) {
        hideResults();
        return;
      }
      const mySeq = ++fetchSeq;
      showResults();
      setStatus('Searching…');
      try {
        const res = await fetch(`${apiBase}?${qs}`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        if (mySeq !== fetchSeq) return; // a newer request superseded this one
        if (data.error) {
          setStatus('No filter set.');
          resultsGrid.innerHTML = '';
          pager.hidden = true;
          return;
        }
        renderResults(data, page === 1);
        lastPage = page;
        // Prefer explicit pagination.has_more when the webhook supplies it;
        // otherwise infer from page fullness. A full page (== per_page) is
        // assumed to have more behind it; a partial or empty page is the
        // last page. Same fallback prevents a dead "Load more" button when
        // the API claims more but returns zero cards.
        const pageCardCount = (data.cards || []).length;
        const pageFull = pageCardCount >= PER_PAGE;
        if (data.pagination && typeof data.pagination.has_more === 'boolean') {
          lastHasMore = data.pagination.has_more && pageCardCount > 0;
        } else {
          lastHasMore = pageFull;
        }
        pager.hidden = !lastHasMore;
        console.log('[singles-filter] page', page, 'returned', pageCardCount, 'cards; pagination:', data.pagination, '→ lastHasMore:', lastHasMore);
      } catch (err) {
        console.warn('[singles-filter] fetch failed', err);
        setStatus('Couldn\'t load results.');
      }
    }

    function setStatus(msg) {
      if (resultsStatus) resultsStatus.textContent = msg;
    }

    function showResults() {
      resultsRoot.hidden = false;
      // Hide only the Shopify-rendered subtree, not the entire .collection-views
      // host — that wrapper still needs to apply data-view to the filter
      // results so the grid/list toggle keeps working.
      if (shopifyViews) shopifyViews.style.display = 'none';
    }
    function hideResults() {
      resultsRoot.hidden = true;
      if (shopifyViews) shopifyViews.style.display = '';
      if (resultsGrid) resultsGrid.innerHTML = '';
      if (resultsList) resultsList.innerHTML = '';
      setStatus(' ');
      pager.hidden = true;
    }

    // Always rebuild the image URL from the canonical id so set + cardno are
    // zero-padded to 3 digits regardless of how the API serialises image_url.
    function cardImageUrlSmall(cardId) {
      const parts = String(cardId || '').split('-');
      if (parts.length !== 2) return '';
      const setId = parts[0].padStart(3, '0');
      const cardNo = parts[1].padStart(3, '0');
      return `https://wrskn.io/${setId}/full/${cardNo}_s.jpg`;
    }

    function formatCardId(cardId) {
      const parts = String(cardId || '').split('-');
      if (parts.length !== 2) return String(cardId || '');
      return `${parts[0].padStart(3, '0')}-${parts[1].padStart(3, '0')}`;
    }

    // Hide off-shelf cardno ranges: 300-899 inclusive. 0-299 and 900+ render.
    function shouldShowCard(cardId) {
      const parts = String(cardId || '').split('-');
      if (parts.length !== 2) return false;
      const cardNo = parseInt(parts[1], 10);
      if (!Number.isFinite(cardNo)) return false;
      return cardNo < 300 || cardNo >= 900;
    }

    function renderCardItem(c) {
      const paddedId = formatCardId(c.id);
      const img = cardImageUrlSmall(c.id);
      const kicker = `${paddedId} : 1X FOIL`;
      return `
        <a class="product-card lift product-card--aspect-63-88" href="/products/${esc(c.id)}" aria-label="View ${esc(c.name)}">
          <div class="ph tint product-card-image" style="aspect-ratio: 63/88;">
            <img src="${esc(img)}" alt="${esc(c.name)}" loading="lazy" sizes="(min-width: 1100px) 22vw, (min-width: 700px) 30vw, 90vw">
            ${foilUrl ? `
              <div class="product-card-overlay-wrap" style="--lumi-mask: url('${esc(img)}');">
                <img class="product-card-overlay" src="${esc(foilUrl)}" alt="" aria-hidden="true" loading="lazy">
              </div>
            ` : ''}
          </div>
          <div class="product-card-kicker mono-sm">${esc(kicker)}</div>
          <h3 class="product-card-title">${esc(c.name)}</h3>
          <div class="product-card-price" data-dynamic-product-price="${esc(c.id)}">
            <span data-dynamic-price-now>…</span>
            <span class="product-card-price-was" data-dynamic-price-was></span>
          </div>
          <button type="button" class="product-card-quick-add" data-card-quick-add-id="${esc(c.id)}" aria-label="Add ${esc(c.name)} to loadout">
            <span class="qa-icon qa-plus" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </span>
            <span class="qa-icon qa-check" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3 7l3 3 5-6" stroke="#639735" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
        </a>
      `;
    }

    // Singles list-row matching snippets/product-list-row.liquid's singles
    // variant. data-card-quick-add-id triggers the document-level handler
    // that fetches /products/<id>.js to resolve a variant before adding.
    function renderListItem(c) {
      const paddedId = formatCardId(c.id);
      return `
        <a href="/products/${esc(c.id)}" class="list-row list-row--singles" data-product-id="filter-${esc(c.id)}" aria-label="View ${esc(c.name)}">
          <div class="list-row-meta">
            <span class="list-row-sku mono-sm">${esc(paddedId)}</span>
            <span class="list-row-variant mono-sm">1X FOIL</span>
          </div>
          <span class="list-row-name">${esc(c.name)}</span>
          <span class="list-row-price num" data-dynamic-product-price="${esc(c.id)}">
            <span class="list-row-price-now" data-dynamic-price-now>…</span>
            <span class="list-row-price-was" data-dynamic-price-was></span>
          </span>
          <div class="list-row-action">
            <button type="button" class="btn btn-sm list-row-add" data-card-quick-add-id="${esc(c.id)}" aria-label="Add ${esc(c.name)} to loadout">Add</button>
          </div>
        </a>
      `;
    }

    function renderResults(data, replace) {
      const all = data.cards || [];
      const cards = all.filter(c => shouldShowCard(c.id));
      const gridHtml = cards.map(renderCardItem).join('');
      const listHtml = cards.map(renderListItem).join('');
      if (replace) {
        if (resultsGrid) resultsGrid.innerHTML = gridHtml;
        if (resultsList) resultsList.innerHTML = listHtml;
      } else {
        if (resultsGrid) resultsGrid.insertAdjacentHTML('beforeend', gridHtml);
        if (resultsList) resultsList.insertAdjacentHTML('beforeend', listHtml);
      }
      // Hide the trailing N cells that don't fill a complete row in the
      // grid. Stays put in the DOM; reveals next time renderResults runs
      // (after Load more) with a fresh tail count.
      applyTailHiding();
      // Status reflects what the USER sees (visible rows × cols), not the
      // total rendered, since the trailing partial row is hidden.
      const grid = resultsGrid;
      if (grid) {
        const visible = grid.querySelectorAll(':scope > *:not([data-row-tail])').length;
        setStatus(`${visible} result${visible === 1 ? '' : 's'}`);
      } else {
        setStatus(`${cards.length} result${cards.length === 1 ? '' : 's'}`);
      }
    }

    // Walk the grid children and tag the trailing partial-row items with
    // data-row-tail (display:none via CSS). Count of hidden = total % cols.
    function applyTailHiding() {
      if (!resultsGrid) return;
      const cols = currentGridCols();
      const items = Array.from(resultsGrid.children);
      const total = items.length;
      const tail = cols > 0 ? total % cols : 0;
      items.forEach((el, i) => {
        if (i >= total - tail && tail > 0) {
          el.setAttribute('data-row-tail', '');
        } else {
          el.removeAttribute('data-row-tail');
        }
      });
    }
    function currentGridCols() {
      if (!resultsGrid) return 0;
      // Computed grid-template-columns reads as e.g. "200px 200px 200px ..."
      // — token count == rendered column count, no matter which media-query
      // breakpoint won.
      const tracks = getComputedStyle(resultsGrid).gridTemplateColumns;
      if (!tracks || tracks === 'none') return 0;
      return tracks.split(' ').filter(Boolean).length;
    }
    // Re-evaluate hiding on viewport resize so a breakpoint change doesn't
    // leave the tail desync'd with the new column count.
    on(window, 'resize', () => {
      clearTimeout(applyTailHiding._t);
      applyTailHiding._t = setTimeout(applyTailHiding, 120);
    });

    // Document-level delegation for the Load more click — survives any
    // re-render of the surrounding container and any future DOM swap.
    // Verbose console trace so we can see what's happening on a stuck click.
    on(document, 'click', async (e) => {
      const btn = e.target.closest('[data-singles-filter-load-more]');
      if (!btn) return;
      e.preventDefault();
      console.log('[singles-filter] load more clicked', { lastPage, lastHasMore, btn });
      if (!lastHasMore) {
        console.warn('[singles-filter] load more: lastHasMore is false — bailing');
        return;
      }
      if (btn.disabled) {
        console.warn('[singles-filter] load more: button already disabled (in flight)');
        return;
      }
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Loading…';
      try {
        console.log('[singles-filter] fetching page', lastPage + 1);
        await runFetch(lastPage + 1);
        console.log('[singles-filter] fetch complete', { lastPage, lastHasMore });
      } catch (err) {
        console.error('[singles-filter] load more fetch threw', err);
      } finally {
        btn.textContent = originalText;
        btn.disabled = false;
      }
    });
  });

  // Delegated quick-add for filter-rendered cards. The existing in-page
  // .product-card-quick-add handler (further down) reads a baked-in variant
  // id from data-card-quick-add; filter cards only know the SET-CARD handle,
  // so this handler fetches /products/<handle>.js to discover the first
  // available variant, then adds it to the cart.
  on(document, 'click', async (e) => {
    const qa = e.target.closest('[data-card-quick-add-id]');
    if (!qa) return;
    e.preventDefault();
    e.stopPropagation();
    if (qa.classList.contains('is-busy') || qa.classList.contains('is-added')) return;
    qa.classList.add('is-busy');
    const cardId = qa.dataset.cardQuickAddId;
    try {
      const pRes = await fetch(`/products/${encodeURIComponent(cardId)}.js`);
      if (!pRes.ok) throw new Error('Product fetch HTTP ' + pRes.status);
      const product = await pRes.json();
      const variant = (product.variants || []).find(v => v.available) || (product.variants || [])[0];
      if (!variant) throw new Error('No variant');
      await addToCart({ id: variant.id, quantity: 1 }, { openDrawer: false, refreshDrawer: false });
      qa.classList.remove('is-busy');
      qa.classList.add('is-added');
      try { syncCartCount(); } catch (_) {}
      try { refreshCartDrawer(); } catch (_) {}
      setTimeout(() => { qa.classList.remove('is-added'); }, 1200);
    } catch (err) {
      qa.classList.remove('is-busy');
      console.warn('[filter-quick-add] failed for', cardId, err);
    }
  });

  /* -----------------------------------------------------------------------
     Bulk-add (collection) — adds N copies of every product in a collection
     to the cart in a single /cart/add.js request.
     ----------------------------------------------------------------------- */
  async function fetchAllCollectionProducts(handle) {
    const out = [];
    let page = 1;
    let pageCount = 0;
    do {
      const res = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`);
      if (!res.ok) throw new Error('Products fetch failed: HTTP ' + res.status);
      const data = await res.json();
      const products = (data && data.products) || [];
      out.push(...products);
      pageCount = products.length;
      page++;
    } while (pageCount === 250);
    return out;
  }

  /* Quick-add (+) buttons on singles-aspect product cards. Delegated so
     it works for cards rendered server-side and any future cards added
     to the DOM. Click adds 1 of the first variant; the button briefly
     swaps to a green check and back via the .is-added class. */
  on(document, 'click', (e) => {
    const qaBtn = e.target.closest('.product-card-quick-add');
    if (!qaBtn) return;
    e.preventDefault();
    e.stopPropagation();
    if (qaBtn.disabled) return;
    const variantId = qaBtn.dataset.cardQuickAdd;
    if (!variantId) return;
    qaBtn.disabled = true;
    addToCart(
      { id: Number(variantId), quantity: 1 },
      { openDrawer: false, refreshDrawer: false }
    )
      .then(() => {
        qaBtn.classList.add('is-added');
        try { syncCartCount(); } catch (err) { /* non-fatal */ }
        try { refreshCartDrawer(); } catch (err) { /* non-fatal */ }
        setTimeout(() => {
          qaBtn.classList.remove('is-added');
          qaBtn.disabled = false;
        }, 280);
      })
      .catch((err) => {
        console.warn('[quick-add] failed:', err);
        qaBtn.disabled = false;
      });
  });

  $$('[data-bulk-add]').forEach((btn) => {
    on(btn, 'click', async () => {
      const handle = btn.dataset.collectionHandle;
      const multiplier = parseInt(btn.dataset.multiplier, 10) || 1;
      if (!handle) return;

      const originalText = btn.textContent.trim();
      btn.disabled = true;
      btn.textContent = 'Loading collection…';

      try {
        const products = await fetchAllCollectionProducts(handle);
        const items = products
          .map((p) => p.variants && p.variants[0] && p.variants[0].id)
          .filter(Boolean)
          .map((id) => ({ id, quantity: multiplier }));

        if (!items.length) {
          btn.textContent = 'No items found';
          setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 2200);
          return;
        }

        btn.textContent = `Adding ${items.length}…`;
        const addRes = await fetch(routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!addRes.ok) {
          const errData = await addRes.json().catch(() => ({}));
          throw new Error(errData.description || 'Add failed: HTTP ' + addRes.status);
        }

        btn.textContent = `Added ${items.length} ✓`;
        try { await refreshCartDrawer(); } catch (e) { /* non-fatal */ }
        try { openCart(); } catch (e) { /* non-fatal */ }
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2200);
      } catch (err) {
        console.warn('[bulk-add] failed:', err);
        btn.textContent = 'Could not add — try again';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2800);
      }
    });
  });

  /* =======================================================================
     PDP — "View product description" modal
     ======================================================================= */
  /* =======================================================================
     Digital PDP — prefill WAX wallet input from the Loot session, if any.
     The Loot redeem module mirrors session.actor into localStorage under
     'warsaken-wax-account' on login/restore and clears it on logout.
     ======================================================================= */
  (function initPdpWalletPrefill() {
    const input = document.querySelector('[data-wallet-input]');
    if (!input) return;
    let saved = '';
    try { saved = localStorage.getItem('warsaken-wax-account') || ''; } catch (_) {}
    if (saved && !input.value) {
      input.value = saved;
      input.setAttribute('data-prefilled', '1');
      const hint = document.querySelector('[data-wallet-hint]');
      if (hint) {
        hint.textContent = 'Prefilled from your connected Loot wallet — change if you want delivery to a different account.';
        hint.classList.add('pdp-wallet-hint--prefilled');
      }
    }
    // Drop the prefilled flag once the user edits — keeps the highlight
    // accurate to "this came from us" vs. "the user typed it".
    on(input, 'input', () => {
      input.removeAttribute('data-prefilled');
      const hint = document.querySelector('[data-wallet-hint]');
      if (hint) {
        hint.textContent = 'Items deliver to this WAX account.';
        hint.classList.remove('pdp-wallet-hint--prefilled');
      }
    });
  })();

  (function initPdpDescriptionModal() {
    const modal = $('[data-desc-modal]');
    if (!modal) return;
    const scrim = $('.pdp-desc-scrim');
    const triggers = $$('[data-desc-open]');
    const closers = $$('[data-desc-close]');
    if (!triggers.length) return;

    let lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      modal.hidden = false;
      // next frame so the .open class triggers the transition
      requestAnimationFrame(() => {
        modal.classList.add('open');
        if (scrim) scrim.classList.add('open');
      });
      document.documentElement.style.overflow = 'hidden';
      const closeBtn = modal.querySelector('.pdp-desc-close');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      modal.classList.remove('open');
      if (scrim) scrim.classList.remove('open');
      document.documentElement.style.overflow = '';
      // wait for transition before hiding
      setTimeout(() => { modal.hidden = true; }, 240);
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
    }

    triggers.forEach((t) => on(t, 'click', (e) => { e.preventDefault(); open(); }));
    closers.forEach((c) => on(c, 'click', (e) => { e.preventDefault(); close(); }));

    on(document, 'keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  })();

  /* Announcement bar.
     Two motion modes, both progressively enhanced (the first item is
     server-rendered .is-active, so static content shows with no JS):
       • slide  — cross-fade through the items on an interval, hover to pause.
       • ticker — clone the items into a seamless marquee. We grow one "group"
                  by repeating items until it fills the viewport, then duplicate
                  the group and translateX by exactly one group width so the
                  loop is seamless. Duration = groupWidth / speed(px per s).
     Reduced-motion: slide stops on the first item; ticker renders static. */
  (function initAnnouncementBar() {
    const bars = $$('[data-announcement]');
    if (!bars.length) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    bars.forEach((bar) => {
      const mode = bar.dataset.mode;
      const viewport = $('.announcement-bar__viewport', bar);
      const track = $('.announcement-bar__track', bar);
      if (!track) return;
      const items = $$('.announcement-bar__item', track);
      if (!items.length) return;

      if (mode === 'slide') {
        if (reduce || items.length < 2) return;
        const sec = (parseFloat(bar.dataset.slideSeconds) || 4) * 1000;
        let idx = 0;
        let timer = null;
        const advance = () => {
          items[idx].classList.remove('is-active');
          idx = (idx + 1) % items.length;
          items[idx].classList.add('is-active');
        };
        const start = () => { timer = window.setInterval(advance, sec); };
        const stop = () => { window.clearInterval(timer); timer = null; };
        start();
        on(bar, 'mouseenter', stop);
        on(bar, 'mouseleave', start);
        // Pause when the tab is hidden so it doesn't jump on return.
        on(document, 'visibilitychange', () => {
          if (document.hidden) stop();
          else if (!timer) start();
        });
        return;
      }

      if (mode === 'ticker') {
        if (reduce) return;
        const speed = parseFloat(bar.dataset.tickerSpeed) || 60; // px per second
        const originalHTML = track.innerHTML;

        // Clones are decorative duplicates — hide them from AT and tab order.
        const clone = (n) => {
          const c = n.cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          c.setAttribute('tabindex', '-1');
          return c;
        };

        const build = () => {
          bar.classList.remove('is-running');
          track.innerHTML = originalHTML;
          const vw = viewport.clientWidth;
          // Grow one group by repeating the original items until it fills the viewport.
          const group = Array.from(track.children).map((n) => n.cloneNode(true));
          let guard = 0;
          while (track.scrollWidth < vw && guard < 40) {
            group.forEach((n) => track.appendChild(clone(n)));
            guard++;
          }
          const groupWidth = track.scrollWidth;
          if (!groupWidth) return;
          // Duplicate the filled group once for a seamless wrap.
          Array.from(track.children).forEach((n) => track.appendChild(clone(n)));
          track.style.setProperty('--ann-ticker-distance', groupWidth + 'px');
          track.style.setProperty('--ann-ticker-duration', groupWidth / speed + 's');
          bar.classList.add('is-running');
        };

        build();
        let resizeTimer = null;
        window.addEventListener('resize', () => {
          window.clearTimeout(resizeTimer);
          resizeTimer = window.setTimeout(build, 200);
        });
      }
    });
  })();

})();
